import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Modal,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Autocomplete,
  Button,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Alert
} from '@mui/material';
import Swal from 'sweetalert2';
import CommentIcon from '@mui/icons-material/Comment';
import { useUser } from '../contexts/UserContext';
import { useAccesos } from '../contexts/AccesosContext';
import GrupoAnalitos from './GrupoAnalitos';

// ⚡ CONTROL DE DEBUGGING DE PERFORMANCE
// Cambia a true SOLO para depurar problemas de performance específicos
const DEBUG_PERFORMANCE = false;

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  maxHeight: '90vh',
  overflowY: 'auto',
  backgroundColor: 'white',
  padding: '20px',
  borderRadius: '8px'
};

const fixedButtonsStyle = {
  position: 'sticky',
  top: '0px',
  right: '0px',
  zIndex: 1000,
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '10px',
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
  padding: '15px 20px',
  borderRadius: '0 0 8px 8px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  backdropFilter: 'blur(5px)',
  borderBottom: '1px solid #e0e0e0',
  marginBottom: '20px'
};

const Resultados = ({ open, handleClose, OrdenTrabajo, analisisFiltrados = null }) => {
  const { tienePermiso } = useAccesos();
  // Mensaje informativo para el desarrollador
  React.useEffect(() => {
    if (DEBUG_PERFORMANCE) {
      console.log("🚀 [PERF-DEBUG] Modo debugging de performance ACTIVADO en Resultados.jsx");
      console.log("💡 Para desactivar logs y mejorar performance, cambiar DEBUG_PERFORMANCE a false");
    }
  }, []);
  
  const server = process.env.REACT_APP_API_ROUTE;
  const [encabezado, setEncabezado] = useState(null);
  const [detalles, setDetalles] = useState([]);
  const [cliente, setCliente] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAdicionalesModal, setShowAdicionalesModal] = useState(false);
  const { user } = useUser();
  // Opciones para el combobox de Equipo y Método (desde la API)
  const [equipoOptions, setEquipoOptions] = useState([]);
  const [metodoOptions, setMetodoOptions] = useState([]);
  const [moduloOptions, setModuloOptions] = useState([]);
  // Estados locales para selección por grupo
  const [selectedEquipos, setSelectedEquipos] = useState({});
  const [selectedMetodos, setSelectedMetodos] = useState({});
  const [selectedModulo, setSelectedModulo] = useState(""); // Sin filtro inicial
  const [selectedTopEquipo, setSelectedTopEquipo] = useState(""); // Sin filtro global inicial
  const [selectedIndividualEquipos, setSelectedIndividualEquipos] = useState({}); // Filtros individuales por grupo
  // Nuevo estado para almacenar el comentario resultado por grupo
  const [comentariosResultado, setComentariosResultado] = useState({});
  const [detallesOriginales, setDetallesOriginales] = useState([]);
  const [comentarioVisible, setComentarioVisible] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [localResultados, setLocalResultados] = useState({}); // clave: `${idMuestra}-${idValorRef}`
  const [localComentarios, setLocalComentarios] = useState({}); // Estado local para comentarios
  const [selectedAnalitos, setSelectedAnalitos] = useState([]); // Para selecciones múltiples
  const [selectAll, setSelectAll] = useState(false); // Para seleccionar todos
  
  // 🔥 NUEVO: Estado para rastrear guardados en curso
  const [guardadosEnCurso, setGuardadosEnCurso] = useState(0); // Contador de operaciones pendientes
  const guardadosEnCursoRef = useRef(0); // Referencia para actualización inmediata

  // Referencias para debouncing optimizado
  const resultadoTimeouts = useRef({});
  const comentarioTimeouts = useRef({});
  const comentarioResultadoTimeouts = useRef({}); // Nuevo timeout para comentarios resultado
  
  // Referencias adicionales para optimización de performance
  const inputChangeTimeouts = useRef({});
  const lastInputValues = useRef({});
  
  // 🔥 CRÍTICO: Referencia para tener siempre el estado más reciente de detalles
  const detallesRef = useRef(detalles);
  
  // 🎯 NAVEGACIÓN GLOBAL: Referencias para manejar navegación entre todos los grupos
  const globalInputRefs = useRef({});
  const globalResultadoFieldIds = useRef([]);
  const lastNavigationTime = useRef(0); // Para evitar navegaciones múltiples rápidas
  
  // Actualizar la referencia cada vez que detalles cambie
  useEffect(() => {
    detallesRef.current = detalles;
  }, [detalles]);

  // 🎯 NAVEGACIÓN GLOBAL: Manejador principal de navegación con teclado
  const handleGlobalKeyDown = useCallback((e, currentId, isAutocompleteOpen) => {
    // Evitar navegaciones múltiples muy rápidas (debounce de 100ms)
    const now = Date.now();
    if (now - lastNavigationTime.current < 100) {
      if (DEBUG_PERFORMANCE) {
        console.log('🎯 Navegación bloqueada por debounce:', currentId);
      }
      return;
    }
    lastNavigationTime.current = now;
    
    // Si el autocomplete está abierto, no intervenir
    if (isAutocompleteOpen) {
      if (DEBUG_PERFORMANCE) {
        console.log('🎯 Autocomplete abierto, delegando navegación local para:', currentId);
      }
      return;
    }

    const currentIndex = globalResultadoFieldIds.current.indexOf(currentId);
    
    if (currentIndex === -1) {
      if (DEBUG_PERFORMANCE) {
        console.log('🎯 Campo no encontrado en lista global:', currentId, 'Lista actual:', globalResultadoFieldIds.current);
      }
      return;
    }

    // Verificar si solo hay un campo total (caso especial)
    if (globalResultadoFieldIds.current.length === 1) {
      if (DEBUG_PERFORMANCE) {
        console.log('🎯 Solo hay un campo global, no hay navegación posible');
      }
      e.preventDefault(); // Evitar comportamiento predeterminado del TAB
      return;
    }

    let targetIndex = -1;

    // TAB: ir al siguiente campo
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      targetIndex = (currentIndex + 1) % globalResultadoFieldIds.current.length; // Navegación circular
      if (DEBUG_PERFORMANCE) {
        console.log('🎯 TAB: navegando de índice', currentIndex, 'a', targetIndex);
      }
    }
    // SHIFT+TAB: ir al campo anterior
    else if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      targetIndex = currentIndex === 0 
        ? globalResultadoFieldIds.current.length - 1 
        : currentIndex - 1;
      if (DEBUG_PERFORMANCE) {
        console.log('🎯 SHIFT+TAB: navegando de índice', currentIndex, 'a', targetIndex);
      }
    }
    // Flecha ABAJO: ir al siguiente campo
    else if (e.key === 'ArrowDown') {
      e.preventDefault();
      targetIndex = (currentIndex + 1) % globalResultadoFieldIds.current.length;
      if (DEBUG_PERFORMANCE) {
        console.log('🎯 ARROW_DOWN: navegando de índice', currentIndex, 'a', targetIndex);
      }
    }
    // Flecha ARRIBA: ir al campo anterior  
    else if (e.key === 'ArrowUp') {
      e.preventDefault();
      targetIndex = currentIndex === 0 
        ? globalResultadoFieldIds.current.length - 1 
        : currentIndex - 1;
      if (DEBUG_PERFORMANCE) {
        console.log('🎯 ARROW_UP: navegando de índice', currentIndex, 'a', targetIndex);
      }
    }

    // Enfocar el campo objetivo
    if (targetIndex !== -1) {
      const targetId = globalResultadoFieldIds.current[targetIndex];
      const targetInput = globalInputRefs.current[targetId];
      
      if (targetInput) {
        // Usar un pequeño delay para asegurar que el focus se aplique correctamente
        setTimeout(() => {
          targetInput.focus();
          if (DEBUG_PERFORMANCE) {
            console.log('🎯 Navegación global exitosa (con delay):', currentId, '->', targetId);
            console.log('🎯 Campo enfocado ahora:', document.activeElement);
          }
        }, 0);
      } else {
        if (DEBUG_PERFORMANCE) {
          console.log('🎯 Campo objetivo no encontrado:', targetId);
          console.log('🎯 Referencias disponibles:', Object.keys(globalInputRefs.current));
          console.log('🎯 Lista global:', globalResultadoFieldIds.current);
        }
        
        // Buscar el siguiente campo disponible (máximo 5 intentos para evitar bucle infinito)
        let found = false;
        for (let i = 1; i <= Math.min(5, globalResultadoFieldIds.current.length - 1); i++) {
          const nextIndex = (targetIndex + i) % globalResultadoFieldIds.current.length;
          const nextId = globalResultadoFieldIds.current[nextIndex];
          const nextInput = globalInputRefs.current[nextId];
          
          if (nextInput && nextId !== currentId) {
            setTimeout(() => {
              nextInput.focus();
              if (DEBUG_PERFORMANCE) {
                console.log('🎯 Navegación global (campo alternativo con delay):', currentId, '->', nextId, `(intento ${i})`);
              }
            }, 0);
            found = true;
            break;
          } else if (DEBUG_PERFORMANCE) {
            console.log(`🎯 Intento ${i}: ${nextId} no disponible`);
          }
        }
        
        if (!found) {
          if (DEBUG_PERFORMANCE) {
            console.log('🎯 No se encontró ningún campo alternativo disponible');
          }
          // Como último recurso, buscar cualquier campo disponible
          const availableRefs = Object.keys(globalInputRefs.current);
          if (availableRefs.length > 0) {
            const firstAvailable = availableRefs.find(ref => ref !== currentId);
            if (firstAvailable) {
              setTimeout(() => {
                globalInputRefs.current[firstAvailable].focus();
                if (DEBUG_PERFORMANCE) {
                  console.log('🎯 Navegación global (último recurso con delay):', currentId, '->', firstAvailable);
                }
              }, 0);
            }
          }
        }
      }
    }
  }, []);

  // Funciones auxiliares para extraer valores (movidas antes para uso en useEffect)
  const extractResultado = useCallback((resultado) => {
    if (resultado && typeof resultado === "object" && !Array.isArray(resultado)) {
      return resultado.resultadoPredeterminado ? resultado.resultadoPredeterminado : "";
    }
    return resultado || "";
  }, []);

  const extractComentario = useCallback((comentario) => {
    if (comentario && typeof comentario === "object" && !Array.isArray(comentario)) {
      return comentario.comentarioPredeterminado ? comentario.comentarioPredeterminado : "";
    }
    return comentario || "";
  }, []);

  useEffect(() => {
    const inicialResultados = {};
    const inicialComentarios = {};
    detalles.forEach(d => {
      const key = `${d.idMuestra}-${d.id}`;
      // Usar las funciones de extracción para obtener los valores correctos
      inicialResultados[key] = extractResultado(d.resultado) || '';
      inicialComentarios[key] = extractComentario(d.comentario) || '';
    });
    setLocalResultados(inicialResultados);
    setLocalComentarios(inicialComentarios);
  }, [detalles]);

  // 🔥 Función INMEDIATA para guardar resultado (sin debouncing)
  const saveResultadoInmediato = useCallback(async (idMuestra, idValorReferenciaAnalito, value) => {
    const key = `${idMuestra}-${idValorReferenciaAnalito}`;
    
    // 🔥 Incrementar contador de guardados en curso
    guardadosEnCursoRef.current += 1;
    setGuardadosEnCurso(prev => prev + 1);
    console.log(`🔄 Guardado iniciado para ${key}. Total en curso: ${guardadosEnCursoRef.current}`);
    
    if (DEBUG_PERFORMANCE) {
      console.log(`[PERF-DEBUG] 🚀 saveResultadoInmediato - ${key} - Iniciando persistencia`);
      console.time(`persistencia-${key}`);
    }
    
    try {
      // ✅ CRÍTICO: Usar detallesRef.current para obtener el estado MÁS RECIENTE
      const detalleActual = detallesRef.current.find(d => d.idMuestra === idMuestra && d.id === idValorReferenciaAnalito);
      if (!detalleActual) {
        console.error(`❌ No se encontró detalle para ${key}`);
        return;
      }

      const tieneResultado = detalleActual.idResultado !== null && detalleActual.idResultado !== undefined;
      const apiUrl = tieneResultado
        ? `${server}/MgAnalisisGeneralResultadosAnalitoes/${detalleActual.idResultado}`
        : `${server}/MgAnalisisGeneralResultadosAnalitoes`;
      const method = tieneResultado ? "PUT" : "POST";

      const requestBody = {
        ...(tieneResultado ? { id: detalleActual.idResultado } : {}),
        idAnalito: detalleActual.idAnalito,
        idTipoAnalisis: detalleActual.idTipoAnalisis,
        resultado: value,
        idMuestraXAnalisis: idMuestra,
        idValorReferenciaAnalito: idValorReferenciaAnalito,
        comentario: detalleActual.comentario || "",
        idExamen: detalleActual.idExamen,
        ...(tieneResultado
          ? { modificadoPor: user?.codigoUsuario || "ADMIN" }
          : { ingresadoPor: user?.codigoUsuario || "ADMIN" }),
        idEquipo: selectedTopEquipo !== "ALL" && selectedTopEquipo !== "" ? selectedTopEquipo : detalleActual.idEquipo,
        metodo: detalleActual.metodo
      };

      const response = await fetch(apiUrl, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error al actualizar resultado:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      // 🔥 MANEJO ROBUSTO: Verificar si hay contenido antes de parsear JSON
      const contentType = response.headers.get("content-type");
      let responseData = null;
      let nuevoIdResultado = detalleActual.idResultado;
      
      if (contentType && contentType.includes("application/json")) {
        const responseText = await response.text();
        if (responseText && responseText.trim() !== "") {
          try {
            responseData = JSON.parse(responseText);
            nuevoIdResultado = responseData.id || responseData.idResultado || detalleActual.idResultado;
          } catch (jsonError) {
            console.warn('⚠️ Respuesta no es JSON válido, pero guardado exitoso (HTTP 200)');
            // Si el servidor respondió OK pero sin JSON, asumimos éxito
          }
        } else {
          console.warn('⚠️ Respuesta vacía, pero guardado exitoso (HTTP 200)');
        }
      } else {
        console.warn('⚠️ Respuesta no es JSON, pero guardado exitoso (HTTP 200)');
      }
      
      // Continuar con actualización de estado aunque no haya JSON (si HTTP fue 200)
      setDetalles(prevDetalles => {
        const updated = prevDetalles.map(detalle => {
          if (detalle.idMuestra === idMuestra && detalle.id === idValorReferenciaAnalito) {
            const updatedDetalle = { 
              ...detalle, 
              resultado: value,
              idResultado: nuevoIdResultado
            };
            
            if (value && value.toString().trim() !== "" && !detalle.idEquipo && selectedTopEquipo && selectedTopEquipo !== "" && selectedTopEquipo !== "ALL") {
              updatedDetalle.idEquipo = selectedTopEquipo;
            }
            
            return updatedDetalle;
          }
          return detalle;
        });
        return updated;
      });
      
      setLocalResultados(prev => ({
        ...prev,
        [`resultado-${idMuestra}-${idValorReferenciaAnalito}`]: value
      }));
    } catch (error) {
      console.error(`❌ Error al actualizar resultado ${key}:`, error);
      
      // 🔥 RE-LANZAR el error para que el guardado forzado pueda manejarlo
      throw error; // Esto permite que el try-catch en handleGuardarResultados lo capture
    } finally {
      // 🔥 Decrementar contador de guardados en curso
      guardadosEnCursoRef.current -= 1;
      setGuardadosEnCurso(prev => prev - 1);
      console.log(`✅ Guardado completado para ${key}. Total en curso: ${guardadosEnCursoRef.current}`);
      
      if (DEBUG_PERFORMANCE) {
        console.timeEnd(`persistencia-${key}`);
      }
    }
  }, [server, user, selectedTopEquipo]); // ✅ Removido 'detalles' - ahora usa detallesRef.current

  // Función de debouncing optimizada para resultados - persistencia Y actualización de estado
  const debouncedUpdateResultado = useCallback((idMuestra, idValorReferenciaAnalito, value) => {
    if (DEBUG_PERFORMANCE) {
      console.log(`[PERF-DEBUG] 🎯 INICIO debouncedUpdateResultado - idMuestra: ${idMuestra}, idValor: ${idValorReferenciaAnalito}, value: "${value}"`);
    }
    
    const key = `${idMuestra}-${idValorReferenciaAnalito}`;
    
    // Cancelar timeout anterior si existe
    if (resultadoTimeouts.current[key]) {
      if (DEBUG_PERFORMANCE) {
        console.log(`[PERF-DEBUG] ⏰ Cancelando timeout anterior para ${key}`);
      }
      clearTimeout(resultadoTimeouts.current[key]);
    }
    
    if (DEBUG_PERFORMANCE) {
      console.log(`[PERF-DEBUG] ⏱️ Configurando nuevo timeout de 2 segundos para ${key}`);
    }
    
    // ⚡ OPTIMIZACIÓN: Aumentar timeout a 3 segundos para digitación más fluida
    resultadoTimeouts.current[key] = setTimeout(async () => {
      // Llamar a la función inmediata que hace el trabajo real
      await saveResultadoInmediato(idMuestra, idValorReferenciaAnalito, value);
      delete resultadoTimeouts.current[key];
    }, 3000); // ⚡ Aumentado a 3 segundos para digitación más fluida
    
    if (DEBUG_PERFORMANCE) {
      console.log(`[PERF-DEBUG] ✅ FIN debouncedUpdateResultado (timeout configurado) - ${key}`);
    }
  }, [saveResultadoInmediato]);

  // 🔥 Función INMEDIATA para guardar comentario (sin debouncing)
  const saveComentarioInmediato = useCallback(async (idMuestra, idValorReferenciaAnalito, value) => {
    const key = `${idMuestra}-${idValorReferenciaAnalito}`;
    
    // 🔥 Incrementar contador de guardados en curso
    guardadosEnCursoRef.current += 1;
    setGuardadosEnCurso(prev => prev + 1);
    console.log(`🔄 Guardado de comentario iniciado para ${key}. Total en curso: ${guardadosEnCursoRef.current}`);
    
    try {
      // ✅ CRÍTICO: Usar detallesRef.current para obtener el estado MÁS RECIENTE
      const detalleActual = detallesRef.current.find(d => d.idMuestra === idMuestra && d.id === idValorReferenciaAnalito);
      if (!detalleActual) {
        console.error(`❌ No se encontró detalle para comentario ${key}`);
        return;
      }

      const tieneResultado = detalleActual.idResultado !== null && detalleActual.idResultado !== undefined;
      const apiUrl = tieneResultado
        ? `${server}/MgAnalisisGeneralResultadosAnalitoes/${detalleActual.idResultado}`
        : `${server}/MgAnalisisGeneralResultadosAnalitoes`;
      const method = tieneResultado ? "PUT" : "POST";

      const requestBody = {
        ...(tieneResultado ? { id: detalleActual.idResultado } : {}),
        idAnalito: detalleActual.idAnalito,
        idTipoAnalisis: detalleActual.idTipoAnalisis,
        resultado: extractResultado(detalleActual.resultado) || "",
        idMuestraXAnalisis: idMuestra,
        idValorReferenciaAnalito: idValorReferenciaAnalito,
        comentario: value,
        idExamen: detalleActual.idExamen,
        ...(tieneResultado
          ? { modificadoPor: user?.codigoUsuario || "ADMIN" }
          : { ingresadoPor: user?.codigoUsuario || "ADMIN" }),
        idEquipo: detalleActual.idEquipo,
        metodo: detalleActual.metodo
      };

      const response = await fetch(apiUrl, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error al actualizar comentario:', errorText);
      } else {
        const responseData = await response.json();
        const nuevoIdResultado = responseData.id || responseData.idResultado || detalleActual.idResultado;
        
        setDetalles(prevDetalles => {
          const updated = prevDetalles.map(detalle =>
            detalle.idMuestra === idMuestra && detalle.id === idValorReferenciaAnalito
              ? { 
                  ...detalle, 
                  comentario: value,
                  idResultado: nuevoIdResultado
                }
              : detalle
          );
          return updated;
        });
        
        setLocalComentarios(prev => ({
          ...prev,
          [`comentario-${idMuestra}-${idValorReferenciaAnalito}`]: value
        }));
      }
    } catch (error) {
      console.error('❌ Error al actualizar comentario:', error);
    } finally {
      // 🔥 Decrementar contador de guardados en curso
      guardadosEnCursoRef.current -= 1;
      setGuardadosEnCurso(prev => prev - 1);
      console.log(`✅ Guardado de comentario completado para ${key}. Total en curso: ${guardadosEnCursoRef.current}`);
    }
  }, [server, user, extractResultado]); // ✅ Removido 'detalles' - ahora usa detallesRef.current

  // Función de debouncing optimizada para comentarios
  const debouncedUpdateComentario = useCallback((idMuestra, idValorReferenciaAnalito, value) => {
    const key = `${idMuestra}-${idValorReferenciaAnalito}`;
    
    if (comentarioTimeouts.current[key]) {
      clearTimeout(comentarioTimeouts.current[key]);
    }
    
    comentarioTimeouts.current[key] = setTimeout(async () => {
      await saveComentarioInmediato(idMuestra, idValorReferenciaAnalito, value);
      delete comentarioTimeouts.current[key];
    }, 3000);
  }, [saveComentarioInmediato]);

  // Funciones optimizadas para manejo instantáneo de cambios en inputs
  const handleLocalResultadoChange = useCallback((key, value) => {
    // Actualización INSTANTÁNEA del estado local - sin debouncing
    setLocalResultados(prev => ({ ...prev, [key]: value }));
    
    // Programar persistencia con debouncing largo
    const [idMuestra, idValorReferenciaAnalito] = key.split('-');
    debouncedUpdateResultado(parseInt(idMuestra), parseInt(idValorReferenciaAnalito), value);
  }, [debouncedUpdateResultado]);

  const handleLocalComentarioChange = useCallback((key, value) => {
    // Actualización INSTANTÁNEA del estado local - sin debouncing  
    setLocalComentarios(prev => ({ ...prev, [key]: value }));
    
    // Programar persistencia con debouncing largo
    const [idMuestra, idValorReferenciaAnalito] = key.split('-');
    debouncedUpdateComentario(parseInt(idMuestra), parseInt(idValorReferenciaAnalito), value);
  }, [debouncedUpdateComentario]);

  // ELIMINAR las funciones onBlur - ya no son necesarias
  // const handleResultadoBlur = useCallback((idMuestra, idValorReferenciaAnalito) => {
  //   const key = `${idMuestra}-${idValorReferenciaAnalito}`;
  //   const value = localResultados[key];
  //   debouncedUpdateResultado(idMuestra, idValorReferenciaAnalito, value);
  // }, [localResultados, debouncedUpdateResultado]);

  // const handleComentarioBlur = useCallback((idMuestra, idValorReferenciaAnalito) => {
  //   const key = `${idMuestra}-${idValorReferenciaAnalito}`;
  //   const value = localComentarios[key];
  //   debouncedUpdateComentario(idMuestra, idValorReferenciaAnalito, value);
  // }, [localComentarios, debouncedUpdateComentario]);
  
  const resetFormulario = useCallback(() => {
    setDetalles([]);
    setEncabezado(null);
    setCliente(null);
    setSelectedEquipos({});
    setSelectedMetodos({});
    setSelectedModulo("");
    setSelectedTopEquipo("");
    setSelectedIndividualEquipos({});
    setComentariosResultado({});
    setComentarioVisible({});
    setDetallesOriginales([]);
    setShowAdicionalesModal(false);
    setLocalResultados({});
    setLocalComentarios({});
    
    // Limpiar timeouts pendientes
    Object.values(resultadoTimeouts.current).forEach(clearTimeout);
    Object.values(comentarioTimeouts.current).forEach(clearTimeout);
    Object.values(comentarioResultadoTimeouts.current).forEach(clearTimeout);
    resultadoTimeouts.current = {};
    comentarioTimeouts.current = {};
    comentarioResultadoTimeouts.current = {};
  }, []);

  // Función para actualizar un detalle de orden médica con equipo y método
  const actualizarDetalleOrdenMedica = async (idDetalle, idEquipo, idMetodo) => {
    try {
      console.log(`🔄 actualizarDetalleOrdenMedica - idDetalle: ${idDetalle}, idEquipo: ${idEquipo}, idMetodo: ${idMetodo}`);
      
      const detalleActual = await fetchDetallePorId(idDetalle);
      
      // Verificar si realmente hay cambios
      const equipoActual = detalleActual.idEquipo;
      const metodoActual = detalleActual.idMetodo;
      
      const equipoNuevo = idEquipo !== undefined ? idEquipo : equipoActual;
      const metodoNuevo = idMetodo !== undefined ? idMetodo : metodoActual;
      
      // Normalizar para comparación: null, "", 0 se consideran equivalentes ("sin valor")
      const normalizar = (v) => (v === null || v === undefined || v === "" || v === 0 || v === "0") ? null : v.toString();
      
      console.log(`🔍 Comparación - Equipo actual: ${equipoActual} (${normalizar(equipoActual)}) vs nuevo: ${equipoNuevo} (${normalizar(equipoNuevo)}), Método actual: ${metodoActual} (${normalizar(metodoActual)}) vs nuevo: ${metodoNuevo} (${normalizar(metodoNuevo)})`);
      
      // Solo actualizar si hay cambios reales
      if (normalizar(equipoActual) === normalizar(equipoNuevo) && normalizar(metodoActual) === normalizar(metodoNuevo)) {
        console.log(`ℹ️ No hay cambios en detalle ${idDetalle}, omitiendo actualización`);
        return true;
      }
      
      // Asegurar que el objeto tenga la estructura correcta para el API
      // Usar 0 en lugar de null para campos int del API de C#
      const camposActualizados = {
        ...detalleActual,
        idDetalle: idDetalle,
        idEquipo: equipoNuevo || 0,
        idMetodo: metodoNuevo || 0,
        modificadoPor: user ? user.codigoUsuario : "ADMIN"
      };

      // Solo loggear si estamos debuggeando performance
      if (DEBUG_PERFORMANCE) {
        console.log("[PERF-DEBUG] Payload para actualizar detalle:", {
          idDetalle,
          equipoAnterior: equipoActual,
          equipoNuevo,
          metodoAnterior: metodoActual,
          metodoNuevo,
          modificadoPor: camposActualizados.modificadoPor
        });
      }

      const response = await fetch(`${server}/MgOrdenesMedicasDetalles/${idDetalle}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(camposActualizados)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ PUT falló para detalle ${idDetalle}: ${response.status} - ${errorText}`);
        throw new Error(`Error al actualizar detalle ${idDetalle}: ${response.status} - ${errorText}`);
      }

      console.log(`✅ Detalle ${idDetalle} actualizado exitosamente - equipo: ${camposActualizados.idEquipo}, método: ${camposActualizados.idMetodo}`);
      return true;
    } catch (error) {
      console.error(`❌ Error al actualizar detalle ${idDetalle}:`, error);
      return false;
    }
  };


  // Referencias para evitar bucles infinitos en useEffect
  const comentariosResultadoRef = useRef(comentariosResultado);
  comentariosResultadoRef.current = comentariosResultado;

  useEffect(() => {
    // Para cada detalle, usamos la clave 'descripcionTipoAnalisis - descripcionMuestra'
    // y si el detalle trae el campo comentarioResultado, lo usamos; si no, se asigna cadena vacía.
    const inicialComentarios = {};
    detalles.forEach((detalle) => {
      const key = `${detalle.descripcionTipoAnalisis} - ${detalle.descripcionMuestra}`;
      
      // SOLUCIÓN: Preservar comentarios locales no guardados
      // Solo usar el valor del servidor si no hay un comentario local pendiente
      const comentarioServidor = detalle.comentarioResultado || "";
      const comentarioLocal = comentariosResultadoRef.current[key];
      
      const tieneComentarioLocal = comentarioLocal !== undefined && 
                                   comentarioLocal !== comentarioServidor;
      
      if (tieneComentarioLocal) {
        // Preservar el comentario local si es diferente al del servidor
        inicialComentarios[key] = comentarioLocal;
        if (DEBUG_PERFORMANCE) {
          console.log(`🔒 [PERF-DEBUG] Preservando comentario local para ${key}: "${comentarioLocal}"`);
        }
      } else {
        // Usar el valor del servidor si no hay cambios locales
        inicialComentarios[key] = comentarioServidor;
        if (DEBUG_PERFORMANCE && comentarioServidor) {
          console.log(`📥 [PERF-DEBUG] Usando comentario del servidor para ${key}: "${comentarioServidor}"`);
        }
      }
    });
    
    // Solo actualizar si hay cambios reales para evitar re-renders innecesarios
    const comentariosActuales = JSON.stringify(comentariosResultadoRef.current);
    const comentariosNuevos = JSON.stringify(inicialComentarios);
    
    if (comentariosActuales !== comentariosNuevos) {
      if (DEBUG_PERFORMANCE) {
        console.log(`🔄 [PERF-DEBUG] Actualizando comentarios resultado - cambios detectados`);
      }
      setComentariosResultado(inicialComentarios);
    }
  }, [detalles]);

  const toggleComentarioVisible = useCallback((detalleId) => {
    setComentarioVisible((prev) => ({
      ...prev,
      [detalleId]: !prev[detalleId],
    }));
  }, []);


  // Cargar módulos filtrando solo los que tienen análisis en esta orden
  useEffect(() => {
    const fetchModulos = async () => {
      if (detalles.length === 0) return;
      
      try {
        const response = await fetch(`${server}/MgModuloes/porIdTipo/1`);
        if (response.ok) {
          const data = await response.json();
          const modulos = data.$values ? data.$values : data;
          
          // Filtrar solo módulos que tienen análisis en esta orden
          const modulosEnOrden = modulos.filter(modulo => 
            detalles.some(detalle => detalle.idModulo === modulo.idModulo)
          );
          
          const options = modulosEnOrden.map((modulo) => ({
            id: modulo.idModulo,
            nombre: modulo.nombreModulo
          }));
          
          // REMOVIDO: No agregar "Todos" - solo los módulos específicos
          setModuloOptions(options);
          
          // REMOVIDO: No auto-seleccionar módulo único
          // El usuario debe elegir explícitamente o dejar sin filtro
        } else {
          console.error("Error al obtener módulos");
        }
      } catch (error) {
        console.error("Error al obtener módulos:", error);
      }
    };
    fetchModulos();
  }, [server, detalles]);

  // Cargar opciones de equipos - MANTENER TODOS LOS EQUIPOS DISPONIBLES
  useEffect(() => {
    const fetchEquipos = async () => {
      try {
        const response = await fetch(`${server}/MgEquipoes`);
        if (response.ok) {
          const data = await response.json();
          const equipos = data.$values ? data.$values : data;
          
          // Solo loggear si estamos debuggeando performance
          if (DEBUG_PERFORMANCE) {
            console.log("🔧 [PERF-DEBUG] Cargando TODOS los equipos disponibles para el filtro general");
          }
          
          // Mantener TODOS los equipos disponibles, no filtrar por orden
          const options = equipos.map((equipo) => ({
            id: equipo.idEquipo || equipo.id,
            descripcion: equipo.descripcion
          }));
          
          // Verificar si hay analitos sin equipo solo para agregar la opción
          const analitosSinEquipo = detalles.some(detalle => !detalle.idEquipo);
          if (analitosSinEquipo) {
            options.push({ id: "SIN_EQUIPO", descripcion: "Sin equipo" });
          }
          
          // Solo loggear si estamos debuggeando performance
          if (DEBUG_PERFORMANCE) {
            console.log(`✅ [PERF-DEBUG] Total equipos cargados: ${options.length}`, options.map(o => o.descripcion));
          }
          setEquipoOptions(options);
          
          // Ya no preseleccionamos equipos globalmente - solo individualmente por grupo
        } else {
          console.error("Error al obtener equipos");
        }
      } catch (error) {
        console.error("Error al obtener equipos:", error);
      }
    };

    const fetchMetodo = async () => {
      try {
        const response = await fetch(`${server}/MgAnalisisGeneralMetodo`);
        if (response.ok) {
          const data = await response.json();
          const metodos = data.$values ? data.$values : data;
          const options = metodos.map((metodo) => ({
            id: metodo.id,
            descripcion: metodo.descripcion
          }));
          setMetodoOptions(options);
        } else {
          console.error("Error al obtener métodos");
        }
      } catch (error) {
        console.error("Error al obtener métodos:", error);
      }
    };

    fetchEquipos();
    fetchMetodo();
  }, [server, detalles]); // Agregar 'detalles' como dependencia para la preselección

  // Al abrir, cargar cliente y orden médica
  useEffect(() => {
    const fetchData = async () => {
      if (open && OrdenTrabajo) {
        try {
          const clienteData = await fetchCliente();
          await fetchOrdenMedica(
            OrdenTrabajo.idOrdenMedicaMes,
            clienteData.idSexo,
            clienteData.numeroIdentidad
          );
        } catch (error) {
          console.error("Error al cargar datos:", error);
        }
      }
    };
    fetchData();
  }, [open, OrdenTrabajo]);

  // Inicializar selectedEquipos, selectedMetodos y comentariosResultado: preseleccionar equipos inteligentemente por grupo
  useEffect(() => {
    setSelectedEquipos((prev) => {
      const nuevosSeleccionados = { ...prev };
      
      // Agrupar detalles por grupo
      const grupos = detalles.reduce((acc, detalle) => {
        const groupKey = `${detalle.descripcionTipoAnalisis} - ${detalle.descripcionMuestra}`;
        if (!acc[groupKey]) {
          acc[groupKey] = [];
        }
        acc[groupKey].push(detalle);
        return acc;
      }, {});

      // Para cada grupo, aplicar la lógica de preselección
      Object.entries(grupos).forEach(([groupKey, grupoDetalles]) => {
        if (nuevosSeleccionados[groupKey] === undefined) {
          // 1. PRIMERA PRIORIDAD: Buscar equipo con resultados guardados previamente en este grupo
          const equipoConResultados = grupoDetalles.find(detalle => 
            detalle.resultado && 
            detalle.resultado.toString().trim() !== "" && 
            detalle.idEquipo
          );
          
          if (equipoConResultados && equipoConResultados.idEquipo) {
            // Solo loggear si estamos debuggeando performance
            if (DEBUG_PERFORMANCE) {
              console.log(`✅ [PERF-DEBUG] Equipo con resultados para grupo ${groupKey}:`, equipoConResultados.idEquipo);
            }
            nuevosSeleccionados[groupKey] = equipoConResultados.idEquipo.toString();
            return;
          }
          
          // 2. SEGUNDA PRIORIDAD: Obtener equipos únicos disponibles en este grupo
          const equiposEnGrupo = new Set();
          grupoDetalles.forEach(detalle => {
            if (detalle.idEquipo) {
              equiposEnGrupo.add(detalle.idEquipo.toString());
            } else {
              equiposEnGrupo.add("SIN_EQUIPO");
            }
          });

          // Buscar primer equipo real (no "ALL" ni "SIN_EQUIPO") disponible en este grupo
          const equiposReales = Array.from(equiposEnGrupo).filter(eq => eq !== "SIN_EQUIPO");
          if (equiposReales.length > 0) {
            // Solo loggear si estamos debuggeando performance
            if (DEBUG_PERFORMANCE) {
              console.log(`⚙️ [PERF-DEBUG] Seleccionando primer equipo real para grupo ${groupKey}:`, equiposReales[0]);
            }
            nuevosSeleccionados[groupKey] = equiposReales[0];
            return;
          }
          
          // 3. TERCERA PRIORIDAD: Si solo hay analitos sin equipo
          if (equiposEnGrupo.has("SIN_EQUIPO") && equiposEnGrupo.size === 1) {
            // Solo loggear si estamos debuggeando performance
            if (DEBUG_PERFORMANCE) {
              console.log(`🚫 [PERF-DEBUG] Solo analitos sin equipo en grupo ${groupKey}`);
            }
            nuevosSeleccionados[groupKey] = "SIN_EQUIPO";
            return;
          }
          
          // 4. Por defecto, no seleccionar nada (el usuario debe elegir)
          // Solo loggear si estamos debuggeando performance
          if (DEBUG_PERFORMANCE) {
            console.log(`⚠️ [PERF-DEBUG] Sin preselección automática para grupo ${groupKey} - usuario debe elegir`);
          }
          nuevosSeleccionados[groupKey] = "";
        }
      });

      return nuevosSeleccionados;
    });

    setSelectedMetodos((prev) => {
      const nuevosMetodos = { ...prev };
      
      // Agrupar detalles por grupo
      const grupos = detalles.reduce((acc, detalle) => {
        const groupKey = `${detalle.descripcionTipoAnalisis} - ${detalle.descripcionMuestra}`;
        if (!acc[groupKey]) {
          acc[groupKey] = [];
        }
        acc[groupKey].push(detalle);
        return acc;
      }, {});

      // Para cada grupo, buscar si existe un método guardado previamente
      Object.entries(grupos).forEach(([groupKey, grupoDetalles]) => {
        if (nuevosMetodos[groupKey] === undefined) {
          // Buscar si algún detalle del grupo tiene un método asignado
          const detalleConMetodo = grupoDetalles.find(detalle => 
            detalle.idMetodo && detalle.idMetodo.toString().trim() !== ""
          );
          
          if (detalleConMetodo && detalleConMetodo.idMetodo) {
            // Solo loggear si estamos debuggeando performance
            if (DEBUG_PERFORMANCE) {
              console.log(`✅ [PERF-DEBUG] Método guardado encontrado para grupo ${groupKey}:`, detalleConMetodo.idMetodo);
            }
            nuevosMetodos[groupKey] = detalleConMetodo.idMetodo.toString();
          } else {
            // Solo loggear si estamos debuggeando performance
            if (DEBUG_PERFORMANCE) {
              console.log(`⚠️ [PERF-DEBUG] Sin método guardado para grupo ${groupKey} - dejando vacío`);
            }
            nuevosMetodos[groupKey] = "";
          }
        }
      });

      return nuevosMetodos;
    });

  }, [detalles]);

  // Cleanup de timeouts al desmontar el componente
  useEffect(() => {
    return () => {
      // Limpiar todos los timeouts pendientes al desmontar
      Object.values(resultadoTimeouts.current).forEach(clearTimeout);
      Object.values(comentarioTimeouts.current).forEach(clearTimeout);
      Object.values(comentarioResultadoTimeouts.current).forEach(clearTimeout);
      resultadoTimeouts.current = {};
      comentarioTimeouts.current = {};
      comentarioResultadoTimeouts.current = {};
    };
  }, []);

  const fetchCliente = async () => {
    try {
      const response = await fetch(`${server}/MgClientes/GetByIdCliente/${OrdenTrabajo.idCliente}`);
      if (response.ok) {
        const data = await response.json();
        setCliente(data);
        return data;
      } else {
        throw new Error("Error al obtener el cliente");
      }
    } catch (error) {
      console.error("Error al obtener el cliente:", error);
      throw error;
    }
  };

const fetchOrdenMedica = async (codigo, idSexo, identidad) => {
  setLoading(true);
  if (DEBUG_PERFORMANCE) {
    console.log("[PERF-DEBUG] fetchOrdenMedica:", codigo, idSexo, identidad);
  }
  try {
    const response = await fetch(`${server}/MgOrdenesMedicas/OrdenMedica/${codigo}`);
    if (response.ok) {
      const data = await response.json();

      const edadCliente = calcularEdadEnDias(data.fechaNacimiento);

      setEncabezado({
        cliente: data.nombreCompleto,
        identidad: identidad,
        edad: calcularEdad(data.fechaNacimiento),
        sexo: data.descripcionSexo,
        direccion: data.direccionCompleta,
        telefono: data.telefono,
        correoElectronico: data.correoElectronico,
        fecha: data.fechaOrdenMedica,
        sucursalEntrega: data.sucursalEntrega
      });

      if (DEBUG_PERFORMANCE) {
        console.log("[PERF-DEBUG] Datos orden médica:", data);
        console.log("[PERF-DEBUG] Detalles orden médica:", data.detalle.$values);
      }

      const detallePromises = data.detalle?.$values.map(async (detalle, detalleIndex) => {
        const muestras = detalle.muestras?.$values || [];
        const resultados = detalle.resultados?.$values || [];
        
        // 🔥 NUEVO: Si es remitido sin muestras/VR, crear entrada sintética
        if (detalle.remitido === true ) {
          console.log(`📄 Análisis remitido SIN VR detectado: ${detalle.descripcionTipoAnalisis}`);
          return [{
            id: `remitido-${detalle.idDetalle}`, // ID único sintético
            idDetalle: detalle.idDetalle,
            descripcionMuestra: 'N/A', // Sin muestra específica
            descripcionTipoAnalisis: detalle.descripcionTipoAnalisis,
            idTipoAnalisis: detalle.idTipoAnalisis,
            idValorReferenciaAnalito: null,
            analito: detalle.descripcionTipoAnalisis, // Usar nombre del análisis como analito
            valorReferencia: '', // Sin VR
            descripcionUnidadDeMedida: '',
            esCuantitativo: false,
            posicion: 0,
            comentariosPredeterminados: [],
            resultadoPredeterminado: [],
            adicional: false,
            idEquipo: "",
            metodo: "",
            idMetodo: detalle.idMetodo || null,
            comentarioResultado: detalle.comentarioResultado || '',
            urlResultadoRemitido: detalle.urlResultadoRemitido || null,
            idModulo: detalle.idModulo,
            remitido: true,
            ordenDetalle: detalleIndex,
            // Campos para resultado (aunque no se usan en remitidos)
            resultado: '',
            idExamen: detalle.idDetalle,
            idResultado: null,
            comentario: '',
            idMuestra: 0 // ID sintético para evitar conflictos
          }];
        }
        
        // Flujo normal para análisis con muestras/VR
        const muestraDetalles = await Promise.all(
          muestras.map(async (muestra) => {
            const responseDetalle = await fetch(
              `${server}/MgAnalitoValoresReferenciums/GetByMuestraXAnalisis/${muestra.idMuestraXAnalisis}`
            );
            if (responseDetalle.ok) {
              if (DEBUG_PERFORMANCE) {
                console.log('[PERF-DEBUG] aqui resultados', resultados);
              }
              const dataDetalle = await responseDetalle.json();
              if (DEBUG_PERFORMANCE) {
                console.log('[PERF-DEBUG] aqui data cruda', dataDetalle.$values);
              }

              const registrosFiltrados = dataDetalle.$values.filter((registro) => {
                const validEdad = edadCliente >= registro.edadMinima && edadCliente <= registro.edadMaxima;
                const validSexo = registro.idSexo === null
                  ? true
                  : (registro.idSexo === idSexo || registro.idSexo === 3);
                return validEdad && validSexo;
              });
              
              registrosFiltrados.forEach((registro) => {
                const resultadoCoincidente = resultados.find(
                  (resultado) =>
                    resultado.idMuestraXAnalisis === registro.idMuestra &&
                    resultado.idValorReferenciaAnalito === registro.id
                );
                if (resultadoCoincidente) {
                  registro.resultado = resultadoCoincidente.resultado;
                  registro.idExamen = resultadoCoincidente.idExamen;
                  registro.idResultado = resultadoCoincidente.id;
                  registro.comentario = resultadoCoincidente.comentario;
                } else {
                  registro.idExamen = detalle.idDetalle;
                  registro.comentario = "";
                }
              });
              
              const registrosOrdenados = registrosFiltrados.sort((a, b) => {
                const posicionA = parseInt(a.posicion) || 0;
                const posicionB = parseInt(b.posicion) || 0;
                return posicionA - posicionB;
              });
              
              return registrosOrdenados.map((analito) => ({
                ...analito,
                idDetalle: detalle.idDetalle,
                descripcionMuestra: muestra.descripcionMuestra,
                descripcionTipoAnalisis: detalle.descripcionTipoAnalisis,
                idTipoAnalisis: detalle.idTipoAnalisis,
                idValorReferenciaAnalito: analito.id,
                comentariosPredeterminados: Array.isArray(analito.comentariosPredeterminados)
                  ? analito.comentariosPredeterminados
                  : (analito.comentariosPredeterminados?.$values || []),
                resultadoPredeterminado: Array.isArray(analito.resultadoPredeterminado)
                  ? analito.resultadoPredeterminado
                  : (analito.resultadoPredeterminado?.$values || []),
                adicional: analito.adicional || false,
                idEquipo: analito.idEquipo || "",
                metodo: analito.metodo || "",
                idMetodo: detalle.idMetodo || null,
                comentarioResultado: detalle.comentarioResultado,
                urlResultadoRemitido: detalle.urlResultadoRemitido,
                idModulo: detalle.idModulo,
                remitido: detalle.remitido,
                ordenDetalle: detalleIndex
              }));
            }
            return null;
          })
        );
        return muestraDetalles.flat().filter((d) => d !== null);
      });

      const detallesData = await Promise.all(detallePromises);
      const detallesFinalesOrdenados = detallesData.flat();
      
      console.log("Detalles manteniendo orden original de la API:");
      detallesFinalesOrdenados.slice(0, 10).forEach((d, index) => {
        console.log(`${index}: ${d.descripcionTipoAnalisis} - ${d.analito} (Posición: ${d.posicion || 0}, Orden Detalle: ${d.ordenDetalle}, Remitido: ${d.remitido})`);
      });

      const detallesUnicos = [];
      const clavesUnicas = new Set();

      console.log("Proceso de deduplicación preservando analitos en diferentes tipos de análisis:");
      for (const detalle of detallesFinalesOrdenados) {
        // 🔥 NUEVO: Para remitidos sin VR, usar ID único sintético
        const clave = detalle.remitido && !detalle.idValorReferenciaAnalito
          ? `remitido-${detalle.idDetalle}` // Clave única para remitidos sin VR
          : `${detalle.idTipoAnalisis}-${detalle.idMuestra}-${detalle.idAnalito}-${detalle.idEquipo || 'sin_equipo'}`;
        
        if (!clavesUnicas.has(clave)) {
          clavesUnicas.add(clave);
          detallesUnicos.push(detalle);
          console.log(`Agregando: ${detalle.analito} en análisis ${detalle.descripcionTipoAnalisis} - ${detalle.descripcionMuestra} con posición ${detalle.posicion || 0} (Remitido: ${detalle.remitido})`);
        } else {
          console.log(`Omitiendo duplicado exacto: ${detalle.analito} en análisis ${detalle.descripcionTipoAnalisis} - ${detalle.descripcionMuestra} con posición ${detalle.posicion || 0}`);
        }
      }

      let detallesFinales = detallesUnicos;
      if (Array.isArray(analisisFiltrados) && analisisFiltrados.length > 0) {
        const idsAnalisisFiltrados = analisisFiltrados.map(a => a.idTipoAnalisis);
        detallesFinales = detallesUnicos.filter(detalle =>
          idsAnalisisFiltrados.includes(detalle.idTipoAnalisis)
        );
      }
      
      setDetalles(detallesFinales);
      console.log('DETALLES FINALES MANTENIENDO ORDEN API (incluyendo remitidos sin VR):', detallesFinales);
      
      const inicialComentarioVisible = {};
      detallesFinales.forEach(detalle => {
        if (detalle.comentario && detalle.comentario.toString().trim() !== "") {
          inicialComentarioVisible[detalle.id] = true;
        }
      });
      setComentarioVisible(inicialComentarioVisible);

      const equiposPorGrupo = {};
      detallesFinales.forEach((detalle) => {
        const key = `${detalle.descripcionTipoAnalisis} - ${detalle.descripcionMuestra}`;
        if (!equiposPorGrupo[key]) {
          equiposPorGrupo[key] = detalle.idEquipo?.toString() || "";
        }
      }); 
      setSelectedEquipos(equiposPorGrupo);
      setDetallesOriginales(data.detalle?.$values || []);

      console.log("Detalles procesados manteniendo orden de API:", detallesFinales);
    } else {
      Swal.fire("Error", "No se pudo cargar la información de la orden.", "error");
    }
  } catch (error) {
    console.error("Error al cargar la orden médica:", error);
    Swal.fire("Error", "Ocurrió un error al cargar los datos.", "error");
  } finally {
    setLoading(false);
  }
};

  const calcularEdad = (fechaNacimiento) => {
    if (!fechaNacimiento) return '';

    const nacimiento = new Date(fechaNacimiento);
    const hoy = new Date();

    let años = hoy.getFullYear() - nacimiento.getFullYear();
    let meses = hoy.getMonth() - nacimiento.getMonth();
    let días = hoy.getDate() - nacimiento.getDate();

    // Ajustar días si hoy es antes del día de nacimiento
    if (días < 0) {
      meses--;
      const ultimoDiaMesAnterior = new Date(hoy.getFullYear(), hoy.getMonth(), 0).getDate();
      días += ultimoDiaMesAnterior;
    }

    // Ajustar meses si es antes del mes de nacimiento
    if (meses < 0) {
      años--;
      meses += 12;
    }

    return `${años} años, ${meses} meses y ${días} días`;
  };


  const calcularEdadEnDias = (fechaNacimiento) => {
    if (!fechaNacimiento) return 0;

    const nacimiento = new Date(fechaNacimiento);
    const hoy = new Date();

    const diferenciaEnMilisegundos = hoy - nacimiento;
    const dias = Math.floor(diferenciaEnMilisegundos / (1000 * 60 * 60 * 24));

    return dias;
  };

  // Funciones optimizadas que ahora usan debouncing
  const handleResultadoChange = useCallback((idMuestraXAnalito, idValorReferenciaAnalito, value) => {
    if (DEBUG_PERFORMANCE) {
      console.log(`[PERF-DEBUG] 🎯 INICIO handleResultadoChange - idMuestra: ${idMuestraXAnalito}, idValor: ${idValorReferenciaAnalito}, value: "${value}"`);
      console.time(`handleResultadoChange-${idMuestraXAnalito}-${idValorReferenciaAnalito}`);
    }
    
    const key = `${idMuestraXAnalito}-${idValorReferenciaAnalito}`;
    
    if (DEBUG_PERFORMANCE) {
      console.log(`[PERF-DEBUG] 📝 Actualizando estado local - key: ${key}`);
    }
    
    // Actualizar estado local inmediatamente para UI responsiva
    setLocalResultados(prev => ({ ...prev, [key]: value }));
    
    if (DEBUG_PERFORMANCE) {
      console.log(`[PERF-DEBUG] 🔄 Llamando debouncedUpdateResultado`);
    }
    
    // Aplicar debouncing para actualización del estado global
    debouncedUpdateResultado(idMuestraXAnalito, idValorReferenciaAnalito, value);
    
    if (DEBUG_PERFORMANCE) {
      console.timeEnd(`handleResultadoChange-${idMuestraXAnalito}-${idValorReferenciaAnalito}`);
      console.log(`[PERF-DEBUG] ✅ FIN handleResultadoChange`);
    }
  }, [debouncedUpdateResultado]);

  const handleComentarioChange = useCallback((idMuestraXAnalito, idValorReferenciaAnalito, value) => {
    const key = `${idMuestraXAnalito}-${idValorReferenciaAnalito}`;
    // Actualizar estado local inmediatamente para UI responsiva
    setLocalComentarios(prev => ({ ...prev, [key]: value }));
    // Aplicar debouncing para actualización del estado global
    debouncedUpdateComentario(idMuestraXAnalito, idValorReferenciaAnalito, value);
  }, [debouncedUpdateComentario]);

  // ELIMINADAS: funciones duplicadas movidas arriba antes del useEffect

  const camposDeseados = ["idDetalle", "descripcionTipoAnalisis", "valorVenta", "comentarios", "idEquipo", "ingresadoPor", "idFactura", "idMetodo", "idModulo", "idOrdenMedica", "idTipoAnalisis", "idTipoPrecio", "idTipoAnalisis", "valorDescuento", "valorVenta", "evaluada", "comentarios"];

  const pickDetailFields = (detalle, fields) => {
    return fields.reduce((acc, field) => {
      if (detalle[field] !== undefined) {
        acc[field] = detalle[field];
      }
      return acc;
    }, {});
  };

  const fetchDetallePorId = async (idDetalle) => {
    try {
      const response = await fetch(`${server}/MgOrdenesMedicasDetalles/${idDetalle}`, { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`Error al obtener el detalle con id ${idDetalle}`);
      }
      const detalleData = await response.json();
      console.log("Detalle obtenido:", detalleData);
      return detalleData;
    } catch (error) {
      console.error("Error en fetchDetallePorId:", error);
      throw error;
    }
  };

  const actualizarComentariosResultadosPorGrupo = async () => {
    const grupos = detalles.reduce((acc, detalle) => {
      const groupKey = `${detalle.descripcionTipoAnalisis} - ${detalle.descripcionMuestra}`;
      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push(detalle);
      return acc;
    }, {});

    const updatePromises = Object.entries(comentariosResultado).map(async ([groupKey, comentarioResultado]) => {
      const grupoDetalles = grupos[groupKey];
      if (grupoDetalles && grupoDetalles.length > 0) {
        const idDetalle = grupoDetalles[0].idDetalle;
        const detalleOriginal = await fetchDetallePorId(idDetalle);

        // ✅ Solo verificamos si realmente cambió cuando ambos valores tienen contenido
        // Si el comentario actual tiene contenido, siempre debe guardarse
        const comentarioOriginal = detalleOriginal.comentarioResultado || "";
        const comentarioNuevo = comentarioResultado || "";
        
        // No hacer PUT solo si ambos están vacíos o son exactamente iguales
        if (comentarioOriginal === comentarioNuevo && comentarioNuevo === "") {
          console.log(`ℹ️ Omitiendo actualización para grupo ${groupKey}: ambos comentarios están vacíos`);
          return; // No hacer PUT si ambos están vacíos
        }
        
        if (comentarioOriginal === comentarioNuevo && comentarioNuevo !== "") {
          console.log(`ℹ️ Omitiendo actualización para grupo ${groupKey}: comentario sin cambios`);
          return; // No hacer PUT si son iguales y tienen contenido
        }

        const payload = {
          ...pickDetailFields(detalleOriginal, camposDeseados),
          comentarioResultado: comentarioNuevo,
          modificadoPor: user ? user.codigoUsuario : 'ADMIN'
        };

        console.log(`💾 Actualizando comentarioResultado para grupo ${groupKey} (idDetalle: ${idDetalle}):`, {
          comentarioOriginal: comentarioOriginal,
          comentarioNuevo: comentarioNuevo,
          payload: payload
        });

        const response = await fetch(`${server}/MgOrdenesMedicasDetalles/${idDetalle}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`No se pudo actualizar el comentario para el idDetalle ${idDetalle}: ${response.status} - ${errorText}`);
        }
        
        console.log(`✅ Comentario del resultado actualizado exitosamente para grupo ${groupKey}`);
      }
    });

    await Promise.all(updatePromises);
    console.log("🎉 Proceso de actualización de comentarios del resultado completado");
  };

  // Nueva función para guardar solo un comentario resultado específico sin validación
  const guardarComentarioResultadoSolo = useCallback(async (groupKey, comentarioNuevo) => {
    try {
      const grupos = detalles.reduce((acc, detalle) => {
        const currentGroupKey = `${detalle.descripcionTipoAnalisis} - ${detalle.descripcionMuestra}`;
        if (!acc[currentGroupKey]) {
          acc[currentGroupKey] = [];
        }
        acc[currentGroupKey].push(detalle);
        return acc;
      }, {});

      const grupoDetalles = grupos[groupKey];
      if (grupoDetalles && grupoDetalles.length > 0) {
        const idDetalle = grupoDetalles[0].idDetalle;
        const detalleOriginal = await fetchDetallePorId(idDetalle);

        const comentarioOriginal = detalleOriginal.comentarioResultado || "";
        const comentarioFinal = comentarioNuevo || "";
        
        // Solo actualizar si hay cambios
        if (comentarioOriginal === comentarioFinal) {
          return;
        }

        const payload = {
          ...pickDetailFields(detalleOriginal, camposDeseados),
          comentarioResultado: comentarioFinal,
          modificadoPor: user ? user.codigoUsuario : 'ADMIN'
        };

        const response = await fetch(`${server}/MgOrdenesMedicasDetalles/${idDetalle}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          console.log(`✅ Comentario del resultado guardado para grupo ${groupKey}`);
        }
      }
    } catch (error) {
      console.error("Error al guardar comentario resultado:", error);
    }
  }, [detalles, server, user, camposDeseados]);

  // Nueva función para guardar solo los métodos que han cambiado
  const guardarSoloMetodos = useCallback(async () => {
    try {
      const grupos = detalles.reduce((acc, detalle) => {
        const groupKey = `${detalle.descripcionTipoAnalisis} - ${detalle.descripcionMuestra}`;
        if (!acc[groupKey]) {
          acc[groupKey] = [];
        }
        acc[groupKey].push(detalle);
        return acc;
      }, {});

      const updatePromises = Object.entries(selectedMetodos).map(async ([groupKey, metodoId]) => {
        if (metodoId && metodoId !== "") {
          const grupoDetalles = grupos[groupKey];
          if (grupoDetalles && grupoDetalles.length > 0) {
            const primerDetalle = grupoDetalles[0];
            console.log(`🔄 Guardando método ${metodoId} para grupo ${groupKey}, detalle ${primerDetalle.idDetalle}`);
            
            try {
              const exito = await actualizarDetalleOrdenMedica(primerDetalle.idDetalle, primerDetalle.idEquipo, metodoId);
              if (!exito) {
                throw new Error(`No se pudo actualizar el método para el grupo ${groupKey}`);
              }
              return { success: true, groupKey };
            } catch (error) {
              console.error(`Error al actualizar método en grupo ${groupKey}:`, error);
              return { success: false, groupKey, error: error.message };
            }
          }
        }
      }).filter(Boolean);

      const results = await Promise.all(updatePromises);
      const failures = results.filter(r => !r.success);
      
      if (failures.length > 0) {
        Swal.fire("Error", `No se pudieron actualizar algunos métodos: ${failures.map(f => f.groupKey).join(', ')}`, "error");
      } else if (results.length > 0) {
        Swal.fire({
          icon: "success",
          title: "Métodos guardados correctamente",
          showConfirmButton: false,
          timer: 1500,
          timerProgressBar: true
        });
      }
    } catch (error) {
      console.error("Error al guardar métodos:", error);
      Swal.fire("Error", "No se pudieron guardar los métodos", "error");
    }
  }, [detalles, selectedMetodos, user]);


  const haCambiado = (detalle) => {
    const original = detallesOriginales
      .flatMap(d => d.resultados?.$values || [])
      .find(r => r.id === detalle.idResultado);

    const resultadoActual = extractResultado(detalle.resultado);
    const comentarioActual = extractComentario(detalle.comentario);

    if (!original) return true; // Si no existe el original, consideralo como cambiado

    const resultadoOriginal = extractResultado(original.resultado);
    const comentarioOriginal = extractComentario(original.comentario);

    return resultadoActual !== resultadoOriginal || comentarioActual !== comentarioOriginal;
  };


  const handleGuardarResultados = async () => {
    console.log("🚀 Iniciando proceso de guardado...");
    
    // 🔥 SOLUCIÓN DEFINITIVA: Esperar tiempo suficiente para capturar TODOS los debounce (1500ms en GrupoAnalitos.jsx) + overhead
    console.log("⏳ Esperando 2000ms para capturar TODOS los eventos onChange/onBlur pendientes (debounce 1.5s)...");
    await new Promise(resolve => setTimeout(resolve, 2000)); // Debe ser > 1500ms del debounce en GrupoAnalitos.jsx
    
    // Ahora sí, capturar los timeouts que se acaban de registrar
    console.log("🔍 Capturando timeouts pendientes...");
    const pendingResultados = Object.keys(resultadoTimeouts.current);
    const pendingComentarios = Object.keys(comentarioTimeouts.current);
    
    console.log(`📝 Timeouts pendientes - Resultados: ${pendingResultados.length}, Comentarios: ${pendingComentarios.length}`);
    
    // ⚡ CAMBIO CRÍTICO: Guardar SECUENCIALMENTE en lugar de en paralelo
    // Esto asegura que cada guardado vea el estado actualizado del anterior
    
    // Forzar guardado de resultados pendientes UNO POR UNO
    let erroresGuardado = [];
    
    for (const key of pendingResultados) {
      const [idMuestra, idValorReferenciaAnalito] = key.split('-').map(Number);
      const value = localResultados[`resultado-${idMuestra}-${idValorReferenciaAnalito}`];
      
      if (value !== undefined) {
        console.log(`⚡ Forzando guardado de resultado ${key}: "${value}"`);
        clearTimeout(resultadoTimeouts.current[key]);
        delete resultadoTimeouts.current[key];
        
        try {
          // ✅ ESPERAR cada guardado antes de continuar con el siguiente
          await saveResultadoInmediato(idMuestra, idValorReferenciaAnalito, value);
          console.log(`✅ Guardado forzado completado para ${key}`);
        } catch (error) {
          console.error(`❌ Error al forzar guardado de ${key}:`, error);
          erroresGuardado.push({ key, value, error: error.message });
        }
        
        // 🔥 Pausa aumentada entre guardados forzados para garantizar completitud
        await new Promise(resolve => setTimeout(resolve, 150)); // Aumentado de 100ms a 150ms
      }
    }
    
    // 🔥 Si hubo errores, mostrarlos y detener el proceso
    if (erroresGuardado.length > 0) {
      const listaErrores = erroresGuardado.map(e => `• ${e.key}: "${e.value}" - ${e.error}`).join('<br>');
      await Swal.fire({
        icon: 'error',
        title: 'Error al guardar resultados',
        html: `
          <p><strong>Los siguientes resultados NO se guardaron:</strong></p>
          <div style="text-align: left; margin: 10px 0;">${listaErrores}</div>
          <p>Por favor, verifique su conexión y vuelva a intentar.</p>
        `,
        confirmButtonText: 'Entendido'
      });
      setGuardando(false);
      return; // 🛑 Detener el proceso de guardado
    }
    
    // Forzar guardado de comentarios pendientes UNO POR UNO
    for (const key of pendingComentarios) {
      const [idMuestra, idValorReferenciaAnalito] = key.split('-').map(Number);
      const value = localComentarios[`comentario-${idMuestra}-${idValorReferenciaAnalito}`];
      
      if (value !== undefined) {
        console.log(`⚡ Forzando guardado de comentario ${key}: "${value}"`);
        clearTimeout(comentarioTimeouts.current[key]);
        delete comentarioTimeouts.current[key];
        
        // ✅ ESPERAR cada guardado antes de continuar con el siguiente
        await saveComentarioInmediato(idMuestra, idValorReferenciaAnalito, value);
        console.log(`✅ Guardado forzado de comentario completado para ${key}`);
        
        // 🔥 Pausa aumentada entre guardados forzados para garantizar completitud
        await new Promise(resolve => setTimeout(resolve, 150)); // Aumentado de 100ms a 150ms
      }
    }
    
    if (pendingResultados.length > 0 || pendingComentarios.length > 0) {
      console.log(`✅ Todos los guardados forzados completados: ${pendingResultados.length + pendingComentarios.length} total`);
    }
    
    // 🔥 VALIDACIÓN MEJORADA: Usar estados locales en lugar de esperar actualización de 'detalles'
    // Esto permite validar inmediatamente después del guardado forzado
    
    const analitosParaGuardar = detallesRef.current.filter((detalle) => detalle.adicional === false);
    
    // Verificar resultados usando ESTADO LOCAL (más preciso y actual)
    const analitosConResultado = analitosParaGuardar.filter(detalle => {
      // Primero verificar en estado local
      const keyResultado = `resultado-${detalle.idMuestra}-${detalle.id}`;
      const resultadoLocal = localResultados[keyResultado];
      
      // Si hay resultado local, contarlo como válido
      if (resultadoLocal !== undefined && resultadoLocal !== null && resultadoLocal.toString().trim() !== "") {
        console.log(`✅ Resultado local encontrado para ${keyResultado}: "${resultadoLocal}"`);
        return true;
      }
      
      // Si no hay local, verificar en detalles persistidos
      const resultadoFinal = extractResultado(detalle.resultado);

      if (detalle.idResultado) {
        // PUT: solo si hubo cambios
        return haCambiado(detalle);
      } else {
        // POST: solo si hay resultado
        return resultadoFinal && resultadoFinal.toString().trim() !== "";
      }
    });

    // Verificar si hay análisis remitidos (pueden guardarse sin resultado)
    const analitosRemitidos = analitosParaGuardar.filter(detalle => detalle.remitido === true);

    // Verificar si hay cambios en comentarios resultado (sin validar resultados individuales)
    const hayComentariosResultadoCambiados = Object.values(comentariosResultado).some(comentario => 
      comentario && comentario.trim() !== ""
    );

    // Verificar si hay cambios en métodos (verificando si algún grupo tiene un método seleccionado diferente a vacío)
    const hayMetodosCambiados = Object.values(selectedMetodos).some(metodo => 
      metodo && metodo !== ""
    );

    // 🔥 VALIDACIÓN INTELIGENTE: Distinguir entre modo EDICIÓN vs modo NUEVO
    
    // Contar resultados ya existentes en el servidor (modo EDICIÓN)
    const resultadosExistentes = analitosParaGuardar.filter(detalle => 
      detalle.idResultado !== null && detalle.idResultado !== undefined
    );
    
    console.log("📊 VALIDACIÓN:");
    console.log("  - Analitos con resultado/cambios:", analitosConResultado.length);
    console.log("  - Resultados ya existentes (EDICIÓN):", resultadosExistentes.length);
    console.log("  - Analitos remitidos:", analitosRemitidos.length);
    console.log("  - Comentarios resultado cambiados:", hayComentariosResultadoCambiados);
    console.log("  - Métodos cambiados:", hayMetodosCambiados);
    console.log("  - Resultados locales:", Object.keys(localResultados).filter(k => k.startsWith('resultado-') && localResultados[k]).length);

    // 🎯 LÓGICA MEJORADA:
    // 1. Si hay resultados existentes → Modo EDICIÓN → Permitir guardar siempre (puede estar editando comentarios, métodos, etc.)
    // 2. Si NO hay resultados existentes → Modo NUEVO → Requerir al menos un resultado nuevo
    const modoEdicion = resultadosExistentes.length > 0;
    
    if (!modoEdicion) {
      // Modo NUEVO: Validar que haya al menos un resultado/cambio
      if (analitosConResultado.length === 0 && !hayComentariosResultadoCambiados && !hayMetodosCambiados && analitosRemitidos.length === 0) {
        Swal.fire("Validación", "Debe ingresar al menos un resultado antes de guardar.", "warning");
        return;
      }
    }
    // Si modoEdicion = true, siempre permitir guardar (puede estar editando)

    if (guardando) return; // Evita múltiples clics rápidos
    setGuardando(true);

    try {
      // 🔥 OPTIMIZACIÓN: Los resultados y comentarios YA fueron guardados en el paso anterior (guardado forzado)
      // Solo necesitamos guardar comentarios resultado y métodos que no se guardan con debouncing
      
      console.log("💾 Guardado forzado completado. Procediendo con comentarios resultado y métodos...");
      
      // ✅ Ya no necesitamos guardar resultados individuales aquí - ya se guardaron arriba
      const allSuccessful = true; // El guardado forzado anterior ya completó exitosamente

      if (allSuccessful) {
        try {
          // Guardar comentarios resultado de cada grupo y métodos
          await actualizarComentariosResultadosPorGrupo();
          
          // 🔥 VERIFICACIÓN FINAL EXTENDIDA: Esperar por guardados automáticos tardíos (debounce 1.5s)
          console.log("⏳ Verificación final: esperando 2000ms por guardados automáticos tardíos (debounce 1.5s)...");
          await new Promise(resolve => setTimeout(resolve, 2000)); // Debe ser > 1500ms del debounce
          
          // 🔥 VERIFICACIÓN ADICIONAL: Si aún hay guardados iniciándose, esperar otro ciclo completo
          if (guardadosEnCursoRef.current > 0) {
            console.log(`⚠️ Se detectaron ${guardadosEnCursoRef.current} guardados en curso después de espera inicial`);
            console.log("⏳ Esperando 1000ms adicionales...");
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
          
          // Determinar mensaje basado en lo que se guardó
          const totalGuardados = pendingResultados.length + pendingComentarios.length;
          let mensaje = "Guardado exitoso.";
          
          if (totalGuardados > 0) {
            mensaje = `${totalGuardados} resultado${totalGuardados > 1 ? 's' : ''} guardado${totalGuardados > 1 ? 's' : ''} correctamente.`;
          } else if (hayComentariosResultadoCambiados || hayMetodosCambiados) {
            mensaje = "Comentarios y métodos actualizados correctamente.";
          } else if (modoEdicion) {
            mensaje = "Cambios guardados correctamente.";
          }
          
          console.log(`✅ ${mensaje}`);
          
          // 🔥 CRÍTICO: Esperar a que todos los guardados automáticos terminen
          if (guardadosEnCursoRef.current > 0) {
            console.log(`⏳ Esperando a que terminen ${guardadosEnCursoRef.current} guardado(s) automático(s) en curso...`);
            
            // Mostrar mensaje de espera
            Swal.fire({
              title: 'Finalizando guardado...',
              html: `Esperando ${guardadosEnCursoRef.current} guardado(s) automático(s) pendiente(s)...`,
              allowOutsideClick: false,
              allowEscapeKey: false,
              didOpen: () => {
                Swal.showLoading();
              }
            });
            
            // Polling cada 200ms hasta que todos terminen (máximo 30 segundos)
            let intentos = 0;
            const maxIntentos = 150; // 150 * 200ms = 30 segundos
            
            while (guardadosEnCursoRef.current > 0 && intentos < maxIntentos) {
              await new Promise(resolve => setTimeout(resolve, 200));
              intentos++;
              console.log(`⏳ Aún quedan ${guardadosEnCursoRef.current} guardado(s) pendiente(s)... (intento ${intentos}/${maxIntentos})`);
              
              // Actualizar mensaje
              Swal.update({
                html: `Esperando ${guardadosEnCursoRef.current} guardado(s) automático(s) pendiente(s)...<br><small>(${intentos}/${maxIntentos})</small>`
              });
            }
            
            if (guardadosEnCursoRef.current > 0) {
              console.warn(`⚠️ Timeout alcanzado. Aún quedan ${guardadosEnCursoRef.current} guardados pendientes.`);
            } else {
              console.log("✅ Todos los guardados automáticos completados. Procediendo a cerrar.");
            }
            Swal.close();
          }
          
          Swal.fire({
            icon: "success",
            title: mensaje,
            showConfirmButton: false,
            timer: 1500,
            timerProgressBar: true
          }).then(() => {
            setGuardando(false);
            handleClose();
            resetFormulario();
          });
        } catch (comentarioError) {
          console.error("Error al guardar comentarios del resultado:", comentarioError);
          Swal.fire({
            icon: "warning",
            title: "Resultados guardados, pero hubo problemas con los comentarios",
            text: "Los resultados se guardaron correctamente, pero algunos comentarios del resultado no se pudieron actualizar.",
            confirmButtonText: "Entendido"
          }).then(() => {
            setGuardando(false);
            handleClose();
            resetFormulario();
          });
        }
      } else {
        Swal.fire("Error", "Algunos resultados no se pudieron guardar.", "error");
      }
    } catch (error) {
      console.error("Error al guardar los resultados:", error);
      Swal.fire("Error", "No se pudieron guardar los resultados.", "error");
      setGuardando(false);
    }
    // NO cerrar aquí - se cierra en el then() del Swal después de esperar guardados pendientes
  };
  
  // Optimización crítica: filtros memoizados para evitar recálculos innecesarios
  const detallesFiltrados = useMemo(() => {
    if (DEBUG_PERFORMANCE) {
      console.log("[PERF-DEBUG] 🔍 Aplicando filtro general - Módulo:", selectedModulo, "Equipo:", selectedTopEquipo);
    }
    
    return detalles.filter(detalle => {
      // Solo aplicar filtros si están seleccionados (no vacíos)
      let moduloMatch = true;
      let equipoMatch = true;
      
      // Filtro por módulo solo si hay uno seleccionado
      if (selectedModulo && selectedModulo !== "") {
        moduloMatch = detalle.idModulo === selectedModulo;
      }

      // Filtro de equipo solo si hay uno seleccionado
      if (selectedTopEquipo && selectedTopEquipo !== "") {
        if (selectedTopEquipo === "SIN_EQUIPO") {
          equipoMatch = !detalle.idEquipo; // Solo analitos sin equipo
        } else {
          equipoMatch = detalle.idEquipo?.toString() === selectedTopEquipo.toString();
        }
      }

      return moduloMatch && equipoMatch;
    });
  }, [detalles, selectedModulo, selectedTopEquipo]);
  
  // Optimización con useMemo para detalles fijos y adicionales - DEBE IR ANTES de gruposAgrupados
  const detallesFijos = useMemo(() => {
     return detallesFiltrados.filter((detalle) => {
      // Si es remitido, siempre incluirlo
      if (detalle.remitido === true) {
        return true;
      }
      // Si no es remitido, aplicar filtro normal de adicionales
      return !detalle.adicional || (detalle.adicional === true && detalle.resultado && detalle.resultado.toString().trim() !== "");
    });
  }, [detallesFiltrados]);

  if (DEBUG_PERFORMANCE) {
    console.log(`[PERF-DEBUG] 📊 Resultado filtro general: ${detallesFiltrados.length} de ${detalles.length} analitos mostrados`);
  }

  // Optimización crítica: Memoización de grupos para evitar procesamiento innecesario
  const gruposAgrupados = useMemo(() => {
    if (DEBUG_PERFORMANCE) {
      console.time("gruposAgrupados-memoization");
    }
    
    const grupos = detallesFijos.reduce((groups, detalle) => {
      // La clave del grupo sigue siendo la misma: tipo de análisis + muestra
      const groupKey = `${detalle.descripcionTipoAnalisis} - ${detalle.descripcionMuestra}`;
      if (!groups[groupKey]) {
        groups[groupKey] = {
          items: [],
          minPosicion: Number.MAX_SAFE_INTEGER, // Rastrear la posición mínima del grupo
          ordenDetalle: detalle.ordenDetalle || 0 // Rastrear el orden original del detalle
        };
      }
      
      const posicion = parseInt(detalle.posicion) || 0;
      
      // Actualizar la posición mínima del grupo
      if (posicion < groups[groupKey].minPosicion) {
        groups[groupKey].minPosicion = posicion;
      }
      
      // Mantener la información de orden original
      groups[groupKey].items.push({
        ...detalle,
        posicion: posicion,
        // Añadimos un ID único para cada combinación de analito + tipo de análisis + muestra
        analitoUniqueId: `${detalle.idTipoAnalisis}-${detalle.idMuestra}-${detalle.idAnalito}-${detalle.idEquipo || 'sin_equipo'}`
      });
      
      return groups;
    }, {});
    
    // Convertir a array, ordenar por posición mínima, y volver a convertir a objeto
    const gruposOrdenados = Object.entries(grupos)
      .sort(([keyA, groupA], [keyB, groupB]) => {
        // Primero ordenar por orden del detalle original
        if (groupA.ordenDetalle !== groupB.ordenDetalle) {
          return groupA.ordenDetalle - groupB.ordenDetalle;
        }
        // Luego por posición mínima del primer analito en el grupo
        return groupA.minPosicion - groupB.minPosicion;
      })
      .reduce((acc, [key, group]) => {
        // Solo devolver los items, no la metadata
        acc[key] = group.items;
        return acc;
      }, {});
    
    if (DEBUG_PERFORMANCE) {
      console.timeEnd("gruposAgrupados-memoization");
      console.log(`[PERF-DEBUG] 📊 Grupos creados y ordenados: ${Object.keys(gruposOrdenados).length}`);
      Object.entries(gruposOrdenados).forEach(([key, items]) => {
        console.log(`  - ${key}: ${items.length} analitos, primera posición: ${items[0]?.posicion || 0}`);
      });
    }
    
    return gruposOrdenados;
  }, [detallesFijos]);

  // 🎯 NAVEGACIÓN GLOBAL: Construir lista global de campos de resultado
  useEffect(() => {
    const allFieldIds = [];
    Object.entries(gruposAgrupados).forEach(([groupKey, group]) => {
      // Verificar si el grupo es remitido (si el primer elemento es remitido, todo el grupo es remitido)
      const esGrupoRemitido = group.length > 0 && group[0].remitido === true;
      
      // Depuración: log de grupo y filtrado
      if (DEBUG_PERFORMANCE) {
        console.log(`🎯 Procesando grupo: ${groupKey}, items: ${group.length}, esRemitido: ${esGrupoRemitido}`);
        group.forEach(detalle => {
          console.log(`   - ${detalle.analito} - remitido: ${detalle.remitido}, id: ${detalle.id}`);
        });
      }
      
      // Solo agregar campos si el grupo NO es remitido
      if (!esGrupoRemitido) {
        const groupFieldIds = group
          .filter(detalle => {
            const isNotRemitido = !detalle.remitido;
            if (DEBUG_PERFORMANCE && !isNotRemitido) {
              console.log(`   ↳ Excluyendo ${detalle.analito} (remitido: ${detalle.remitido})`);
            }
            return isNotRemitido;
          })
          .map(detalle => `resultado-${detalle.idMuestra}-${detalle.id}`);
        
        if (DEBUG_PERFORMANCE) {
          console.log(`   ↳ Campos agregados: ${groupFieldIds.length} [${groupFieldIds.join(', ')}]`);
        }
        
        allFieldIds.push(...groupFieldIds);
      } else {
        if (DEBUG_PERFORMANCE) {
          console.log(`   ↳ Grupo remitido completo - NO agregando campos`);
        }
      }
    });
    globalResultadoFieldIds.current = allFieldIds;
    
    if (DEBUG_PERFORMANCE) {
      console.log('🎯 Lista global FINAL de campos actualizada:', allFieldIds);
    } else {
      console.log('🎯 Lista global de campos actualizada. Total campos:', allFieldIds.length);
      console.log('🎯 Lista completa de campos:', allFieldIds);
    }
  }, [gruposAgrupados]);

  // Optimización adicional: Equipos por grupo memoizados
  const equiposPorGrupo = useMemo(() => {
    if (DEBUG_PERFORMANCE) {
      console.time("equiposPorGrupo-memoization");
    }
    
    const equipos = {};
    Object.entries(gruposAgrupados).forEach(([groupKey, group]) => {
      // Obtener equipos únicos en este grupo
      const equiposUnicos = [...new Set(group.map(item => item.idEquipo).filter(Boolean))];
      equipos[groupKey] = equiposUnicos;
    });
    
    if (DEBUG_PERFORMANCE) {
      console.timeEnd("equiposPorGrupo-memoization");
      console.log(`🎯 [PERF-DEBUG] Equipos en uso para filtro general:`, 
        [...new Set(Object.values(equipos).flat())]);
      console.log(`⚙️ [PERF-DEBUG] Equipos disponibles en filtro general:`, equipoOptions);
    }
    
    return equipos;
  }, [gruposAgrupados, equipoOptions]);

  const detallesAdicionales = useMemo(() => {
    return detallesFiltrados.filter((detalle) =>
      detalle.adicional === true && (!detalle.resultado || detalle.resultado.toString().trim() === "")
    );
  }, [detallesFiltrados]);

  const detallesAdicionalesUnicos = useMemo(() => {
    return Array.from(
      new Map(detallesAdicionales.map((item) => [item.id, item])).values()
    );
  }, [detallesAdicionales]);

  const handleSelectAdicional = (analitoId) => {
    setDetalles((prevDetalles) =>
      prevDetalles.map((detalle) =>
        detalle.id === analitoId ? { ...detalle, adicional: false } : detalle
      )
    );
    Swal.fire("Seleccionado", "El analito se ha movido a la lista principal.", "success");
  };
  
  // Función optimizada para manejar la selección individual de analitos (checkbox)
  const handleToggleAnalito = useCallback((analitoId) => {
    setSelectedAnalitos(prevSelected => {
      if (prevSelected.includes(analitoId)) {
        return prevSelected.filter(id => id !== analitoId);
      } else {
        return [...prevSelected, analitoId];
      }
    });
  }, []);
  
  // Función optimizada para seleccionar/deseleccionar todos los analitos
  const handleToggleSelectAll = useCallback(() => {
    if (selectAll) {
      // Si ya estaban todos seleccionados, deseleccionamos todos
      setSelectedAnalitos([]);
    } else {
      // Si no estaban todos seleccionados, seleccionamos todos
      setSelectedAnalitos(detallesAdicionalesUnicos.map(detalle => detalle.id));
    }
    setSelectAll(!selectAll);
  }, [selectAll, detallesAdicionalesUnicos]);
  
  // Función optimizada para guardar todos los analitos seleccionados
  const handleGuardarSeleccionados = useCallback(() => {
    if (selectedAnalitos.length === 0) {
      Swal.fire("Aviso", "No hay analitos seleccionados para guardar.", "info");
      return;
    }
    
    setDetalles((prevDetalles) =>
      prevDetalles.map((detalle) =>
        selectedAnalitos.includes(detalle.id) ? { ...detalle, adicional: false } : detalle
      )
    );
    
    Swal.fire("Guardado", `Se han movido ${selectedAnalitos.length} analito(s) a la lista principal.`, "success");
    // Limpiar selecciones después de guardar
    setSelectedAnalitos([]);
    setSelectAll(false);
  }, [selectedAnalitos]);

  // Función para subir documento PDF
  const handleDocumentUpload = useCallback(async (detalle, file) => {
    try {
      // Validar que tengamos los datos necesarios
      if (!detalle.idDetalle) {
        throw new Error('ID del detalle no disponible');
      }

      if (!user?.codigoUsuario) {
        throw new Error('Usuario no disponible');
      }

      console.log('=== DEBUGGING SUBIDA DE DOCUMENTO ===');
      console.log('Detalle completo:', detalle);
      console.log('ID Detalle:', detalle.idDetalle, 'Tipo:', typeof detalle.idDetalle);
      console.log('Usuario:', user.codigoUsuario, 'Longitud:', user.codigoUsuario.length);
      console.log('Archivo:', file.name, 'Tamaño:', file.size, 'Tipo:', file.type);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('detalleId', detalle.idDetalle.toString());
      formData.append('usuario', user.codigoUsuario);

      // Log para debugging - verificar FormData
      console.log('=== FORMDATA CONTENTS ===');
      for (let pair of formData.entries()) {
        console.log(`${pair[0]}: ${pair[1]} (Tipo: ${typeof pair[1]})`);
      }

      console.log('=== ENVIANDO REQUEST ===');
      console.log('URL:', `${server}/MgOrdenesMedicasDetalles/upload`);

      const response = await fetch(`${server}/MgOrdenesMedicasDetalles/upload`, {
        method: 'POST',
        body: formData
      });

      console.log('=== RESPONSE ===');
      console.log('Status:', response.status);
      console.log('Status Text:', response.statusText);

      if (response.ok) {
        const result = await response.json();
        console.log('Resultado exitoso:', result);
        
        // Actualizar el detalle con la nueva URL
        setDetalles(prevDetalles => {
          return prevDetalles.map(d => {
            if (d.idDetalle === detalle.idDetalle) {
              return { ...d, urlResultadoRemitido: result.UrlFoto };
            }
            return d;
          });
        });

        Swal.fire({
          icon: "success",
          title: "Documento subido exitosamente",
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true
        });
      } else {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(errorText || 'Error al subir el documento');
      }
    } catch (error) {
      console.error('=== ERROR COMPLETO ===');
      console.error('Error al subir documento:', error);
      console.error('Stack trace:', error.stack);
      Swal.fire("Error", `Error al subir documento: ${error.message}`, "error");
    }
  }, [server, user]);

  // Función para eliminar documento PDF
  const handleDocumentDelete = useCallback(async (detalle) => {
    try {
      const result = await Swal.fire({
        title: '¿Está seguro?',
        text: 'Se eliminará el documento PDF permanentemente',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar'
      });

      if (result.isConfirmed) {
        const response = await fetch(`${server}/MgOrdenesMedicasDetalles/updateResultImage/${detalle.idDetalle}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            UrlResultadoRemitido: null,
            ComentarioResultado: detalle.comentarioResultado || '',
            Usuario: user?.codigoUsuario || 'ADMIN'
          })
        });

        if (response.ok) {
          // Actualizar el detalle eliminando la URL
          setDetalles(prevDetalles => {
            return prevDetalles.map(d => {
              if (d.idDetalle === detalle.idDetalle) {
                return { ...d, urlResultadoRemitido: null };
              }
              return d;
            });
          });

          Swal.fire({
            icon: "success",
            title: "Documento eliminado exitosamente",
            showConfirmButton: false,
            timer: 2000,
            timerProgressBar: true
          });
        } else {
          throw new Error('Error al eliminar el documento');
        }
      }
    } catch (error) {
      console.error('Error al eliminar documento:', error);
      Swal.fire("Error", `Error al eliminar documento: ${error.message}`, "error");
    }
  }, [server, user]);

  const renderModalAdicionales = () => (
    <Modal open={true} onClose={() => setShowAdicionalesModal(false)} disableEscapeKeyDown>
      <Box sx={modalStyle}>
        <Typography variant="h6" gutterBottom>
          Seleccione Analitos Adicionales
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox 
                checked={selectAll} 
                onChange={handleToggleSelectAll}
                color="primary"
              />
            }
            label="Seleccionar Todos"
          />
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleGuardarSeleccionados}
            disabled={selectedAnalitos.length === 0}
          >
            Guardar Seleccionados ({selectedAnalitos.length})
          </Button>
        </Box>
        {detallesAdicionalesUnicos.length > 0 ? (
          <TableContainer component={Paper}>
            <Table sx={{ tableLayout: 'fixed', width: '100%' }} size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ width: '5%' }}>
                    <Checkbox 
                      checked={selectAll}
                      onChange={handleToggleSelectAll}
                      color="primary"
                    />
                  </TableCell>
                  <TableCell sx={{ width: '25%' }}>Analito</TableCell>
                  <TableCell sx={{ width: '20%' }}>Resultado</TableCell>
                  <TableCell sx={{ width: '20%' }}>Valor Referencia</TableCell>
                  <TableCell sx={{ width: '25%' }}>Comentario</TableCell>
                  <TableCell sx={{ width: '15%' }}>Acción</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detallesAdicionalesUnicos.map((detalle) => (
                  <TableRow 
                    key={detalle.id}
                    hover
                    onClick={() => handleToggleAnalito(detalle.id)}
                    sx={{ 
                      cursor: 'pointer',
                      backgroundColor: selectedAnalitos.includes(detalle.id) ? '#e3f2fd' : 'inherit' 
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox 
                        checked={selectedAnalitos.includes(detalle.id)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => handleToggleAnalito(detalle.id)}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>{detalle.analito}</TableCell>
                    <TableCell>
                      {detalle.esCuantitativo ? (
                        <TextField
                          value={localResultados[`${detalle.idMuestra}-${detalle.id}`] || ''}
                          onChange={(e) => {
                            const key = `${detalle.idMuestra}-${detalle.id}`;
                            handleLocalResultadoChange(key, e.target.value);
                          }}
                          fullWidth
                          variant="outlined"
                          size="small"
                          disabled={!tienePermiso('Guardar Resultados')}
                        />
                      ) : (
                        <Autocomplete
                          freeSolo
                          disabled={!tienePermiso('Guardar Resultados')}
                          options={
                            Array.isArray(detalle.resultadoPredeterminado)
                              ? detalle.resultadoPredeterminado
                              : (detalle.resultadoPredeterminado?.$values || [])
                          }
                          getOptionLabel={(option) =>
                            typeof option === 'string'
                              ? option
                              : option.resultadoPredeterminado || ''
                          }
                          value={localResultados[`${detalle.idMuestra}-${detalle.id}`] || ''}
                          onChange={(e, newValue) => {
                            const key = `${detalle.idMuestra}-${detalle.id}`;
                            const valor = typeof newValue === 'string' ? newValue : (newValue?.resultadoPredeterminado || '');
                            handleLocalResultadoChange(key, valor);
                          }}
                          onInputChange={(e, newInputValue) => {
                            const key = `${detalle.idMuestra}-${detalle.id}`;
                            handleLocalResultadoChange(key, newInputValue);
                          }}
                          renderInput={(params) => (
                            <TextField {...params} label="Resultado" variant="outlined" fullWidth size="small" />
                          )}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {detalle.valorReferencia} {detalle.descripcionUnidadDeMedida}
                    </TableCell>
                    <TableCell>
                      <Autocomplete
                        freeSolo
                        disabled={!tienePermiso('Guardar Resultados')}
                        options={
                          Array.isArray(detalle.comentariosPredeterminados)
                            ? detalle.comentariosPredeterminados
                            : (detalle.comentariosPredeterminados?.$values || [])
                        }
                        getOptionLabel={(option) =>
                          typeof option === 'string' ? option : option.comentarioPredeterminado || ''
                        }
                        value={localComentarios[`${detalle.idMuestra}-${detalle.id}`] || ''}
                        onChange={(e, newValue) => {
                          const key = `${detalle.idMuestra}-${detalle.id}`;
                          const valor = typeof newValue === 'string' ? newValue : (newValue?.comentarioPredeterminado || '');
                          handleLocalComentarioChange(key, valor);
                        }}
                        onInputChange={(e, newInputValue) => {
                          const key = `${detalle.idMuestra}-${detalle.id}`;
                          handleLocalComentarioChange(key, newInputValue);
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="Comentario" variant="outlined" fullWidth size="small" />
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <Button variant="contained" size="small" onClick={() => handleSelectAdicional(detalle.id)}>
                        Seleccionar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography>No hay analitos adicionales.</Typography>
        )}
        <Grid container spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
          <Grid item>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleGuardarSeleccionados}
              disabled={selectedAnalitos.length === 0}
            >
              Guardar Seleccionados ({selectedAnalitos.length})
            </Button>
          </Grid>
          <Grid item>
            <Button variant="outlined" onClick={() => {
              // Limpiar selecciones al cerrar
              setSelectedAnalitos([]);
              setSelectAll(false);
              setShowAdicionalesModal(false);
            }}>
              Cerrar
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );

  // Manejadores para cambiar Equipo y Método en cada grupo
  const handleEquipoChange = useCallback(async (groupKey, newEquipo) => {
    // Update the local equipment selection for this group
    setSelectedEquipos((prev) => ({ ...prev, [groupKey]: newEquipo }));

    // Also update the top-level equipment filter to match
    setSelectedTopEquipo(newEquipo);

    // Update all detalles in this group to use the new equipment
    // This ensures that new results will use the selected equipment
    const updatedDetalles = detalles.map((detalle) => {
      const currentGroupKey = `${detalle.descripcionTipoAnalisis} - ${detalle.descripcionMuestra}`;
      if (currentGroupKey === groupKey) {
        // Only update equipment for entries without results or with the same equipment as before
        // This prevents accidental changes to results with different equipment
        const hasResult = detalle.resultado && detalle.resultado.toString().trim() !== "";
        if (!hasResult) {
          return { ...detalle, idEquipo: newEquipo && newEquipo !== "" ? newEquipo : null };
        }
      }
      return detalle;
    });
    setDetalles(updatedDetalles);

    // Actualizar los detalles de la orden médica en la base de datos
    const detallesDelGrupo = detalles.filter(detalle => {
      const currentGroupKey = `${detalle.descripcionTipoAnalisis} - ${detalle.descripcionMuestra}`;
      return currentGroupKey === groupKey;
    });

    // Obtener el primer detalle del grupo para actualizar
    if (detallesDelGrupo.length > 0) {
      const primerDetalle = detallesDelGrupo[0];
      const equipoParaGuardar = newEquipo && newEquipo !== "" ? newEquipo : null;
      console.log(`🔄 Actualizando equipo para grupo ${groupKey}, detalle ${primerDetalle.idDetalle}:`,
        equipoParaGuardar === null ? "VACÍO (null)" : equipoParaGuardar);
      
      try {
        const exito = await actualizarDetalleOrdenMedica(primerDetalle.idDetalle, equipoParaGuardar, primerDetalle.idMetodo);
        if (!exito) {
          Swal.fire("Error", "No se pudo actualizar el equipo en la orden médica.", "error");
        }
      } catch (error) {
        console.error("Error al actualizar equipo en detalle:", error);
        Swal.fire("Error", "No se pudo actualizar el equipo en la orden médica.", "error");
      }
    }
  }, [detalles]);

  const handleMetodoChange = useCallback(async (groupKey, newMetodo) => {
    setSelectedMetodos((prev) => ({ ...prev, [groupKey]: newMetodo }));
    
    const updatedDetalles = detalles.map((detalle) => {
      const currentGroupKey = `${detalle.descripcionTipoAnalisis} - ${detalle.descripcionMuestra}`;
      if (currentGroupKey === groupKey) {
        return { 
          ...detalle, 
          metodo: newMetodo,
          idMetodo: newMetodo && newMetodo !== "" ? newMetodo : null 
        };
      }
      return detalle;
    });
    setDetalles(updatedDetalles);

    // Actualizar los detalles de la orden médica en la base de datos
    const detallesDelGrupo = detalles.filter(detalle => {
      const currentGroupKey = `${detalle.descripcionTipoAnalisis} - ${detalle.descripcionMuestra}`;
      return currentGroupKey === groupKey;
    });

    // Obtener el primer detalle del grupo para actualizar
    if (detallesDelGrupo.length > 0) {
      const primerDetalle = detallesDelGrupo[0];
      const metodoParaGuardar = newMetodo && newMetodo !== "" ? newMetodo : null;
      
      console.log(`🔄 Actualizando método para grupo ${groupKey}, detalle ${primerDetalle.idDetalle}:`, 
        metodoParaGuardar === null ? "VACÍO (null)" : metodoParaGuardar);
      
      try {
        const exito = await actualizarDetalleOrdenMedica(primerDetalle.idDetalle, primerDetalle.idEquipo, metodoParaGuardar);
        if (!exito) {
          Swal.fire("Error", "No se pudo actualizar el método en la orden médica.", "error");
        }
      } catch (error) {
        console.error("Error al actualizar método en detalle:", error);
        Swal.fire("Error", "No se pudo actualizar el método en la orden médica.", "error");
      }
    }
  }, [detalles]);

  // Manejador optimizado para actualizar el comentarioResultado de cada grupo
  const handleComentarioResultadoChange = useCallback((groupKey, newValue) => {
    // Actualizar inmediatamente el estado local para respuesta visual rápida
    setComentariosResultado((prev) => ({ ...prev, [groupKey]: newValue }));
    
    // Debouncing para guardar en la base de datos - solo guardar automáticamente si no es vacío
    if (comentarioResultadoTimeouts.current[groupKey]) {
      clearTimeout(comentarioResultadoTimeouts.current[groupKey]);
    }
    
    // Solo hacer el guardado automático si el comentario tiene contenido
    if (newValue && newValue.trim() !== "") {
      comentarioResultadoTimeouts.current[groupKey] = setTimeout(() => {
        guardarComentarioResultadoSolo(groupKey, newValue);
        delete comentarioResultadoTimeouts.current[groupKey];
      }, 1000); // Aumentar a 1 segundo para comentarios resultado ya que son menos críticos
    } else {
      // Si está vacío, solo actualizar el estado pero no hacer llamada a la API
      delete comentarioResultadoTimeouts.current[groupKey];
    }
  }, []);
  
  // Manejador optimizado para el autoselect de módulo
  const handleModuloChange = useCallback((event, newValue) => {
    if (newValue) {
      setSelectedModulo(newValue.id);
    } else {
      setSelectedModulo("");
    }
  }, []);

  // Manejador optimizado para el autoselect de equipo a nivel global
const handleTopEquipoChange = useCallback((event, newValue) => {
  console.log("🔄 Cambio en filtro general de equipo:", newValue);
  
  if (newValue) {
    console.log("✅ Nuevo equipo seleccionado:", newValue.value);
    setSelectedTopEquipo(newValue.value);
    
    // Actualizar las selecciones individuales de cada grupo SOLO si la opción existe
    setSelectedEquipos(prevSelected => {
      const newSelected = { ...prevSelected };
      
      // Para cada grupo, verificar si el equipo seleccionado globalmente existe en sus opciones
      Object.keys(newSelected).forEach(groupKey => {
        // Obtener los detalles de este grupo específico
        const grupoDetalles = detalles.filter(detalle => {
          const currentGroupKey = `${detalle.descripcionTipoAnalisis} - ${detalle.descripcionMuestra}`;
          return currentGroupKey === groupKey;
        });
        
        // Obtener equipos únicos disponibles en este grupo
        const equiposEnGrupo = new Set();
        grupoDetalles.forEach(detalle => {
          if (detalle.idEquipo) {
            equiposEnGrupo.add(detalle.idEquipo.toString());
          } else {
            equiposEnGrupo.add("SIN_EQUIPO");
          }
        });
        
        // Solo actualizar si el equipo seleccionado globalmente existe en este grupo
        if (equiposEnGrupo.has(newValue.value.toString())) {
          console.log(`✅ Actualizando grupo [${groupKey}] con equipo ${newValue.value} (existe en opciones)`);
          newSelected[groupKey] = newValue.value;
        } else {
          console.log(`⚠️ Grupo [${groupKey}] no tiene equipo ${newValue.value}, manteniendo selección actual: ${newSelected[groupKey]}`);
          // NO CAMBIAR - mantener la selección actual tal como está
          // newSelected[groupKey] = newSelected[groupKey]; // Esto es implícito
        }
      });
      
      return newSelected;
    });
    
    // Solo actualizar analitos sin resultados previos y que tengan el equipo disponible
    setDetalles(prevDetalles => 
      prevDetalles.map(detalle => {
        const tieneResultado = detalle.resultado && detalle.resultado.toString().trim() !== "";
        if (!tieneResultado && newValue.value !== "SIN_EQUIPO") {
          // Verificar si este detalle puede usar el equipo seleccionado
          const equipoDisponible = detalle.idEquipo?.toString() === newValue.value.toString() || 
                                   (!detalle.idEquipo && newValue.value === "SIN_EQUIPO");
          
          if (equipoDisponible) {
            console.log(`🔧 Actualizando equipo del analito ${detalle.analito} a ${newValue.value}`);
            return { ...detalle, idEquipo: newValue.value };
          }
        }
        return detalle;
      })
    );
  } else {
    console.log("🔄 Reseteando filtro global");
    setSelectedTopEquipo("");
    // No resetear las selecciones individuales cuando se resetea globalmente
  }
}, [detalles]);

  const handleEquipoAnalitoChange = useCallback((idMuestraXAnalito, idValorReferenciaAnalito, equipoId) => {
    setDetalles((prevDetalles) =>
      prevDetalles.map((detalle) => {
        if (detalle.idMuestra === idMuestraXAnalito && detalle.idValorReferenciaAnalito === idValorReferenciaAnalito) {
          // Actualizamos el equipo específico para este analito
          return { ...detalle, idEquipo: equipoId };
        }
        return detalle;
      })
    );
  }, []);

  return (
    <Modal open={open} onClose={handleClose} disableEnforceFocus>
      <Box sx={modalStyle}>
        <h2 style={{ margin: '0 0 10px 0' }}>Resultados de la Orden</h2>
        
   
        
        {/* Botones fijos en la parte superior */}
        <Box sx={fixedButtonsStyle}>
          {/* 🔥 Indicador de guardados automáticos en curso */}
          {guardadosEnCurso > 0 && (
            <Alert severity="info" sx={{ mr: 2, py: 0.5, alignItems: 'center' }}>
              🔄 Guardando automáticamente... ({guardadosEnCurso} pendiente{guardadosEnCurso > 1 ? 's' : ''})
            </Alert>
          )}
          
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={handleGuardarResultados}
            disabled={guardando || guardadosEnCurso > 0 || !tienePermiso('Guardar Resultados')}
            title={guardadosEnCurso > 0 ? `Esperando que terminen ${guardadosEnCurso} guardado(s) automático(s)...` : ''}
          >
            {guardando 
              ? "Guardando..." 
              : guardadosEnCurso > 0 
                ? `Esperando guardado (${guardadosEnCurso})...` 
                : "Guardar Resultados"}
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            onClick={() => {
              resetFormulario();
              handleClose();
            }}
            disabled={guardando || guardadosEnCurso > 0}
            title={guardadosEnCurso > 0 ? "No se puede cancelar mientras hay guardados en curso" : ''}
          >
            Cancelar
          </Button>
        </Box>
        
        {loading ? (
          <p>Cargando...</p>
        ) : (
          <>
            {encabezado && (
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <TextField size="small" label="Cliente" value={encabezado.cliente} fullWidth InputProps={{ readOnly: true }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField size="small" label="Identidad" value={encabezado.identidad} fullWidth InputProps={{ readOnly: true }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField size="small" label="Edad" value={encabezado.edad} fullWidth InputProps={{ readOnly: true }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField size="small" label="Sexo" value={encabezado.sexo} fullWidth InputProps={{ readOnly: true }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField size="small" label="Dirección" value={encabezado.direccion} fullWidth InputProps={{ readOnly: true }} />
                </Grid>                <Grid item xs={12} md={6}>
                  <Autocomplete
                    size="small"
                    options={moduloOptions}
                    getOptionLabel={(option) => option.nombre || ""}
                    value={moduloOptions.find((mod) => mod.id === selectedModulo) || null}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    onChange={handleModuloChange}
                    disablePortal
                    componentsProps={{
                      popper: {
                        tabIndex: -1
                      }
                    }}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label={`Módulo${selectedModulo && selectedModulo !== "" ? ` (Seleccionado: ${moduloOptions.find(mod => mod.id === selectedModulo)?.nombre || selectedModulo})` : " (Sin filtro)"}`}
                        variant="outlined" 
                        fullWidth 
                        tabIndex={-1}
                        inputProps={{
                          ...params.inputProps,
                          tabIndex: -1
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    size="small"
                    options={(() => {
                      // Obtener equipos únicos de los detalles filtrados por módulo
                      const equiposEnUso = new Set();
                      const detallesFiltradosPorModulo = detalles.filter(detalle => 
                        !selectedModulo || selectedModulo === "" || detalle.idModulo === selectedModulo
                      );
                      
                      detallesFiltradosPorModulo.forEach(detalle => {
                        if (detalle.idEquipo) {
                          equiposEnUso.add(detalle.idEquipo.toString());
                        } else {
                          equiposEnUso.add("SIN_EQUIPO");
                        }
                      });

                      // Filtrar equipoOptions para mostrar solo los que están en uso
                      const equiposDisponibles = equipoOptions.filter(eq => 
                        equiposEnUso.has(eq.id.toString())
                      );

                      // Solo loggear si estamos debuggeando performance
                      if (DEBUG_PERFORMANCE) {
                        console.log(`🎯 [PERF-DEBUG] Equipos en uso para filtro general:`, Array.from(equiposEnUso));
                        console.log(`⚙️ [PERF-DEBUG] Equipos disponibles en filtro general:`, equiposDisponibles);
                      }

                      return [
                        ...equiposDisponibles.map(eq => ({ 
                          label: `${eq.descripcion}${eq.id === "SIN_EQUIPO" ? "" : ` (ID: ${eq.id})`}`, 
                          value: eq.id 
                        }))
                      ];
                    })()}
                    getOptionLabel={(option) => option.label}
                    value={(() => {
                      if (!selectedTopEquipo || selectedTopEquipo === "") return null;
                      
                      const equiposEnUso = new Set();
                      const detallesFiltradosPorModulo = detalles.filter(detalle => 
                        !selectedModulo || selectedModulo === "" || detalle.idModulo === selectedModulo
                      );
                      
                      detallesFiltradosPorModulo.forEach(detalle => {
                        if (detalle.idEquipo) {
                          equiposEnUso.add(detalle.idEquipo.toString());
                        } else {
                          equiposEnUso.add("SIN_EQUIPO");
                        }
                      });

                      const equiposDisponibles = equipoOptions.filter(eq => 
                        equiposEnUso.has(eq.id.toString())
                      );

                      const opciones = [
                        ...equiposDisponibles.map(eq => ({ 
                          label: `${eq.descripcion}${eq.id === "SIN_EQUIPO" ? "" : ` (ID: ${eq.id})`}`, 
                          value: eq.id 
                        }))
                      ];
                      
                      return opciones.find(opt => opt.value.toString() === selectedTopEquipo.toString()) || null;
                    })()}
                    isOptionEqualToValue={(option, value) => option.value.toString() === value.value.toString()}
                    onChange={handleTopEquipoChange}
                    disablePortal
                    componentsProps={{
                      popper: {
                        tabIndex: -1
                      }
                    }}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label={`Equipo General${selectedTopEquipo && selectedTopEquipo !== "" ? ` (Filtro: ${equipoOptions.find(eq => eq.id.toString() === selectedTopEquipo.toString())?.descripcion || selectedTopEquipo})` : " (Sin filtro)"}`}
                        variant="outlined" 
                        fullWidth 
                        tabIndex={-1}
                        inputProps={{
                          ...params.inputProps,
                          tabIndex: -1
                        }}
                      />
                    )}
                  />
                </Grid>
                     {/* Instrucción de ayuda para formato párrafo */}
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>💡 Formato Párrafo:</strong> Para mostrar los resultados en modo párrafo en el reporte impreso, 
            coloca <code>**</code> al inicio y al final del texto del resultado (ejemplo: **texto del resultado**).
          </Typography>
        </Alert>
              </Grid>

            )}
            {detallesAdicionales.length > 0 && (
              <Grid container justifyContent="center" sx={{ mb: 2 }}>
                <Button size="small" variant="outlined" color="primary" onClick={() => setShowAdicionalesModal(true)}>
                  Configurar analitos adicionales
                </Button>
              </Grid>
            )}            {Object.entries(gruposAgrupados)
            // MANTENER EL ORDEN ORIGINAL - no reordenar los grupos por nombre
            // Los analitos dentro de cada grupo ya vienen ordenados por posición desde la API
            .map(([groupKey, group]) => {
              if (DEBUG_PERFORMANCE) {
                console.log(`[PERF-DEBUG] Renderizando grupo: ${groupKey} con ${group.length} analitos`);
              }
              
              return (
                <GrupoAnalitos
                  key={groupKey}
                  groupKey={groupKey}
                  group={group}
                  equipoOptions={equipoOptions}
                  metodoOptions={metodoOptions}
                  comentarioVisible={comentarioVisible}
                  comentariosResultado={comentariosResultado}
                  handleEquipoChange={handleEquipoChange}
                  handleMetodoChange={handleMetodoChange}
                  handleResultadoChange={handleResultadoChange}
                  handleComentarioChange={handleComentarioChange}
                  toggleComentarioVisible={toggleComentarioVisible}
                  handleComentarioResultadoChange={handleComentarioResultadoChange}
                  selectedTopEquipo={selectedTopEquipo}
                  handleEquipoAnalitoChange={handleEquipoAnalitoChange}
                  selectedEquipo={selectedEquipos[groupKey] || ""}
                  selectedMetodo={selectedMetodos[groupKey] || ""}
                  onEquipoChange={(newEquipo) => handleEquipoChange(groupKey, newEquipo)}
                  onMetodoChange={(newMetodo) => handleMetodoChange(groupKey, newMetodo)}
                  // Optimización crítica: pasar estados locales para evitar re-renders innecesarios
                  localResultados={localResultados}
                  localComentarios={localComentarios}
                  // Nuevas props para manejo de documentos
                  onDocumentUpload={handleDocumentUpload}
                  onDocumentDelete={handleDocumentDelete}
                  user={user}
                  // 🎯 NUEVAS PROPS PARA NAVEGACIÓN GLOBAL
                  globalInputRefs={globalInputRefs}
                  globalKeyDownHandler={handleGlobalKeyDown}
                  globalResultadoFieldIds={globalResultadoFieldIds}
                />
              );
            })}

          </>
        )}
        {showAdicionalesModal && renderModalAdicionales()}
        
      </Box>
    </Modal>
  );
};

export default Resultados;


